import { useNavigate, useParams } from 'react-router-dom'
import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import posthog from 'posthog-js';

import { findObjectByKeyValue } from '../components/utils.js';
import { useAuth } from '../hooks/useAuth'; // Corrected import path
import { SocketContext } from "../context/SocketContext.jsx";

import CommentariesBlock from "../components/match/CommentariesBlock";
import LiveMatchStats from "../components/match/LiveMatchStats";
import MatchHeader from "../components/match/MatchHeader";
import MatchTabs, { MATCH_TAB } from "../components/match/MatchTabs";
import MatchActionDock from "../components/match/MatchActionDock";
import SubstitutionSheet from "../components/match/SubstitutionSheet";
import TacticSheet from "../components/match/TacticSheet";
import MatchLineupsPitch from "../components/match/MatchLineupsPitch";
import MatchPreMatchPanel from "../components/match/MatchPreMatchPanel";
import MatchFullTimePanel, { derivePlayerOfTheMatch } from "../components/match/MatchFullTimePanel";
import MatchGlanceCard from "../components/match/MatchGlanceCard";

import { Spinner } from '../components/common/Spinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { handleTacticChange } from '../services/tacticalService';
import { handleSubstitution } from '../services/substitutionService';
import { normalizeLiveCommandAttemptId, resumeGame, pauseGame } from '../services/simulationControlService';
import { useActiveLeague } from '../hooks/useActiveLeague';
import { isGoalRevealingCommentaryEvent, useLiveScore } from '../hooks/useLiveScore';
import {
    getCompletionStatusFromPayload,
    getFinalScoreFromCompletionPayload,
    getMatchIdFromSocketPayload
} from '../utils/matchCompletion';
import {
    normaliseTacticIdForUi,
    patchSavedTacticIntoMatchData,
    tacticIdReverseMapping
} from '../utils/tacticMapping.js';
import {
    deriveScorers,
    getLatestFormalMinute,
    getLatestMinute,
    isHalfTimeBreak
} from '../utils/matchFeed';
import { formatPlayerName } from '../utils/playerName';

// API imports
import { useGetTeamTactics, useSaveTeamTactics, useGetTeamsheet, useSaveTeamsheet } from '../api/teamsApi';
import { useGetMatchById, useGetMatches } from '../api/matchesApi.js';
import { useGetTeamPlayers } from '../api/playersApi.js';
import { useQuickplayKickoff } from '../api/quickplayApi.js';
import { useGetTeams } from '../api/teamsApi.js'; // for league-scoped my team lookup

import tacticData from "../json-data/tactic.json"
import formationsData from "../json-data/field-positions.json";
import TutorialTooltip from '../components/common/TutorialTooltip';
import {
    POST_MATCH_SQUAD_GUIDANCE,
    QuickplayPostMatchActions
} from '../components/quickplay/QuickplayPostMatchActions';

// NEW: Canonicalise match status strings from backend (e.g. "in_progress" -> "inProgress")
const toCanonicalStatus = (rawStatus) => {
    if (!rawStatus) return rawStatus;
    const normalised = rawStatus.toString().trim().toLowerCase().replace(/_/g, ''); // "in_progress" -> "inprogress"
    if (normalised === 'inprogress') return 'inProgress';
    if (normalised === 'waitingforready') return 'waitingForReady';
    if (normalised === 'waitingforengine') return 'waitingForEngine';
    if (normalised === 'engineerror') return 'engineError';
    return rawStatus;
};

// Defensive filtering: canonical active statuses set for frontend gating
const ACTIVE_STATUSES_SET = new Set(['waitingForReady', 'waitingForEngine', 'inProgress']);
const TERMINAL_MATCH_STATUSES_SET = new Set(['completed', 'forfeited', 'cancelled']);
const LIVE_MATCH_STREAM_STALE_MS = 15000;
const LIVE_MATCH_STREAM_STALE_CHECK_MS = 5000;
// Only drives the countdown the manager sees. The engine owns the real deadline
// and sends its own value on the pause event; this is the fallback for older
// engine builds that omit it.
const INJURY_AUTO_RESUME_FALLBACK_SECONDS = 30;
const MATCH_STATUS_PROGRESS_RANK = {
    scheduled: 0,
    waitingForReady: 1,
    waitingForEngine: 2,
    inProgress: 3
};
const SCHEDULED_MATCH_INFO_TEXT = 'Match starts automatically at the scheduled time. Set your lineup before kickoff.';
const SCHEDULED_MATCH_LOCK_INFO_TEXT = 'Match starts automatically at the scheduled time. Lineups lock 15 minutes before kickoff.';
const MATCH_STREAM_TAKEOVER_NOT_ALLOWED_CODE = 'MATCH_STREAM_TAKEOVER_NOT_ALLOWED';
const EMPTY_MATCH_STREAM_TAKEOVER_GUARD = { key: null, matchId: null, state: null };
const BLOCKING_MATCH_STREAM_TAKEOVER_STATES = new Set(['prompt', 'dismissed', 'confirming']);

const isMatchPageVisible = () => {
    if (typeof document === 'undefined') return true;
    return !document.visibilityState || document.visibilityState === 'visible';
};

const shouldAllowStaleMatchStreamTakeover = () => {
    if (!isMatchPageVisible()) return false;
    if (typeof document === 'undefined' || typeof document.hasFocus !== 'function') return true;
    return document.hasFocus();
};

const buildJoinMatchPayload = (matchId, {
    allowSessionTakeover = true,
    takeoverConfirmed = false
} = {}) => {
    const payload = { matchId };

    if (allowSessionTakeover === false) {
        payload.allowSessionTakeover = false;
    }

    if (takeoverConfirmed === true) {
        payload.allowSessionTakeover = true;
        payload.takeoverConfirmed = true;
    }

    return payload;
};

const isMatchStreamTakeoverBlocked = (result) => (
    result?.code === MATCH_STREAM_TAKEOVER_NOT_ALLOWED_CODE ||
    result?.takeoverRequired === true
);

const getMatchStreamTakeoverPromptKey = (matchId, socketId) => (
    `${matchId || 'no-match'}:${socketId || 'no-socket'}`
);

const getMatchStreamJoinMode = (allowSessionTakeover) => (
    allowSessionTakeover ? 'takeover' : 'no-takeover'
);

const buildMatchStreamKey = (matchId, socketId, status, joinMode) => (
    `${matchId}:${socketId || 'connected'}:${status || 'unknown'}:${joinMode || 'unknown'}`
);

const getScheduledMatchInfoText = (match) => (
    match?.preMatchLineupLockEnabled === true
        ? SCHEDULED_MATCH_LOCK_INFO_TEXT
        : SCHEDULED_MATCH_INFO_TEXT
);

export const shouldRenderLiveMatchStats = (rawStatus) => {
    const status = toCanonicalStatus(rawStatus);
    return ACTIVE_STATUSES_SET.has(status);
};

// Persisted Match.stats keys mapped back onto the engine team stat names the
// stats tab components read. Possession is deliberately missing: the engine
// never reports it, so the stored value is only the schema fallback of 50.
const PERSISTED_TEAM_STAT_FIELDS = [
    ['ShotsOn', 'final_shots_on'],
    ['ShotsOff', 'final_shots_off'],
    ['ShotsBlocked', 'final_shots_blocked'],
    ['ShotsTotal', 'final_shots_total'],
    ['Fouls', 'final_fouls'],
    ['YellowCards', 'yellow_cards'],
    ['SuccessfulTackles', 'successful_tackles'],
    ['SuccessfulDribbles', 'successful_dribbles'],
    ['SuccessfulCrosses', 'successful_crosses'],
    ['BallRecoveries', 'ball_recoveries'],
    ['AerialDuelsWon', 'aerial_duels_won'],
    ['XgTotal', 'xg_total'],
    ['SubstitutionsMade', 'substitutions_made']
];

const toPersistedStatNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

/**
 * Rebuilds the `{ stats, players }` team shapes the stats tab reads from the
 * stats and player data persisted on a completed match doc, so a page reload
 * shows the same table the live match ended on. Keys the doc never stored
 * stay absent, which keeps the gated rows hidden on matches completed before
 * the newer team stats were persisted.
 */
export const buildCompletedMatchStatsTeams = (match) => {
    const persisted = match?.stats;
    if (!persisted || typeof persisted !== 'object') return null;

    const buildSideStats = (side) => {
        const stats = {};
        for (const [statsSuffix, engineKey] of PERSISTED_TEAM_STAT_FIELDS) {
            const value = toPersistedStatNumber(persisted[`${side}${statsSuffix}`]);
            if (value !== null) stats[engineKey] = value;
        }
        return stats;
    };

    const homeStats = buildSideStats('home');
    const awayStats = buildSideStats('away');

    // Corners were persisted (with a schema default of 0) long before the rest
    // of the full-time stats. A zero pair on a doc without the newer keys is
    // that default, not a real count, so the corners row stays hidden there.
    const homeCorners = toPersistedStatNumber(persisted.homeCorners);
    const awayCorners = toPersistedStatNumber(persisted.awayCorners);
    const cornersKnown = homeCorners !== null || awayCorners !== null;
    const cornersReal = (homeCorners ?? 0) + (awayCorners ?? 0) > 0;
    const hasNewEraStats = homeStats.final_fouls !== undefined || awayStats.final_fouls !== undefined;
    if (cornersKnown && (cornersReal || hasNewEraStats)) {
        if (homeCorners !== null) homeStats.corners = homeCorners;
        if (awayCorners !== null) awayStats.corners = awayCorners;
    }

    if (Object.keys(homeStats).length === 0 && Object.keys(awayStats).length === 0) return null;

    // getSaves() reads goalkeeper saves off a players array; the persisted
    // summary player data is the only source for them after a reload.
    const buildSidePlayers = (summaryPlayers) => (
        Array.isArray(summaryPlayers)
            ? summaryPlayers.map((player) => ({
                name: player?.name,
                position: player?.position,
                stats: { saves: toPersistedStatNumber(player?.saves) ?? 0 }
            }))
            : []
    );

    return {
        home: { stats: homeStats, players: buildSidePlayers(match?.summaryPlayerData?.homeTeam) },
        away: { stats: awayStats, players: buildSidePlayers(match?.summaryPlayerData?.awayTeam) }
    };
};

const isTerminalMatchStatus = (rawStatus) => {
    if (!rawStatus) return false;
    const normalised = rawStatus.toString().trim().toLowerCase().replace(/_/g, '');
    return TERMINAL_MATCH_STATUSES_SET.has(normalised);
};

const isNonLiveMatchStatus = (rawStatus) => {
    const status = toCanonicalStatus(rawStatus);
    return !!status && !ACTIVE_STATUSES_SET.has(status);
};

const canChangeTacticForStatus = (rawStatus) => {
    const status = toCanonicalStatus(rawStatus);
    return status === 'scheduled' || status === 'inProgress';
};

const canSubstituteForStatus = (rawStatus) => {
    const status = toCanonicalStatus(rawStatus);
    return status === 'scheduled' || status === 'inProgress';
};

const LIVE_COMMAND_ATTEMPT_KEYS = ['liveCommandAttemptId', 'engineBootAttemptId', 'engine_boot_attempt_id'];

const hasLiveCommandAttemptField = (source) => (
    !!source &&
    typeof source === 'object' &&
    LIVE_COMMAND_ATTEMPT_KEYS.some((key) => Object.prototype.hasOwnProperty.call(source, key))
);

const getLiveCommandAttemptId = (source) => {
    if (!source || typeof source !== 'object') return null;
    for (const key of LIVE_COMMAND_ATTEMPT_KEYS) {
        const attemptId = normalizeLiveCommandAttemptId(source[key]);
        if (attemptId) return attemptId;
    }
    return null;
};

const getLiveCommandAttemptPatch = (source) => (
    hasLiveCommandAttemptField(source)
        ? { liveCommandAttemptId: getLiveCommandAttemptId(source) }
        : {}
);

const isFinalOrEngineErrorMatchStatus = (rawStatus) => {
    const status = toCanonicalStatus(rawStatus);
    return isTerminalMatchStatus(status) || status === 'engineError';
};

const ACTION_PAUSE_REASONS = new Set(['substitution', 'tactic_change']);

const normalisePauseReason = (reason) => {
    if (!reason) return null;
    const normalised = String(reason).trim().toLowerCase();
    if (normalised === 'user_paused' || normalised === 'userpause') return 'user_pause';
    if (normalised === 'tacticchange') return 'tactic_change';
    return normalised;
};

export const preserveSpecificPauseReason = (incomingReason, currentReason) => {
    const incoming = normalisePauseReason(incomingReason);
    const current = normalisePauseReason(currentReason);

    if (incoming === 'user_pause' && ACTION_PAUSE_REASONS.has(current)) {
        return current;
    }

    return incoming;
};

export const shouldIgnoreStaleActiveRefetch = (currentStatus, incomingStatus) => {
    const current = toCanonicalStatus(currentStatus);
    const incoming = toCanonicalStatus(incomingStatus);
    if (!isFinalOrEngineErrorMatchStatus(current)) return false;
    if (!incoming) return true;
    return !isFinalOrEngineErrorMatchStatus(incoming);
};

export const getMostAdvancedMatchStatus = (currentStatus, incomingStatus) => {
    const current = toCanonicalStatus(currentStatus);
    const incoming = toCanonicalStatus(incomingStatus);
    if (!incoming) return current;
    if (!current) return incoming;
    if (isFinalOrEngineErrorMatchStatus(current)) return current;
    if (isFinalOrEngineErrorMatchStatus(incoming)) return incoming;

    const currentRank = MATCH_STATUS_PROGRESS_RANK[current];
    const incomingRank = MATCH_STATUS_PROGRESS_RANK[incoming];
    if (currentRank === undefined || incomingRank === undefined) {
        return incoming;
    }

    return incomingRank >= currentRank ? incoming : current;
};

const toSubstitutionCount = (rawCount) => {
    const parsed = Number(rawCount);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.trunc(parsed));
};

// A team's substitution total only ever climbs within a match, and both backend
// writers keep the stored count monotonic for that reason. Engine frames do not
// arrive in order though: a delayed recovery frame or a cached load_game_state
// snapshot can report an older, lower total. Assigning it directly would walk
// the displayed count backward and re-offer a substitution the engine will
// reject, so keep the highest total any source has reported.
export const getMostAdvancedSubstitutionCount = (incomingCount, ...previousCounts) => (
    previousCounts.reduce(
        (mostAdvanced, previousCount) => Math.max(mostAdvanced, toSubstitutionCount(previousCount)),
        toSubstitutionCount(incomingCount)
    )
);

const normalizePlayerName = (rawName) => {
    if (rawName === null || rawName === undefined) return '';
    return String(rawName).replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
};

const normalizeWyId = (rawWyId) => {
    const parsed = Number(rawWyId);
    if (!Number.isFinite(parsed)) return null;
    return Math.trunc(parsed);
};

export const buildPlayersFromLiveState = ({ liveStatePlayers, fullRoster, fallbackTeamsheet }) => {
    const rosterByWyId = new Map();
    const rosterByName = new Map();

    (Array.isArray(fullRoster) ? fullRoster : []).forEach((player) => {
        const wyId = normalizeWyId(player?.wyId);
        if (wyId !== null) {
            rosterByWyId.set(wyId, player);
        }
        const normalizedName = normalizePlayerName(player?.name || player?.shortName);
        if (normalizedName) {
            rosterByName.set(normalizedName, player);
        }
    });

    const formationPlaceByWyId = new Map();
    const teamsheetStatusByWyId = new Map();
    const teamsheetPlayerByWyId = new Map();
    const starters = Array.isArray(fallbackTeamsheet?.players) ? fallbackTeamsheet.players : [];
    const substitutes = Array.isArray(fallbackTeamsheet?.subs) ? fallbackTeamsheet.subs : [];

    starters.forEach((starter) => {
        const starterWyId = normalizeWyId(starter?.wyId);
        if (starterWyId !== null) {
            teamsheetStatusByWyId.set(starterWyId, 1);
            teamsheetPlayerByWyId.set(starterWyId, starter);
            if (typeof starter?.position === 'string') {
                formationPlaceByWyId.set(starterWyId, starter.position);
            }
        }
    });

    substitutes.forEach((substitute) => {
        const substituteWyId = normalizeWyId(substitute?.wyId);
        if (substituteWyId !== null) {
            teamsheetStatusByWyId.set(substituteWyId, 2);
            teamsheetPlayerByWyId.set(substituteWyId, substitute);
        }
    });

    const hydratePlayer = (livePlayer) => {
        const wyId = normalizeWyId(livePlayer?.wyId);
        if (wyId === null || !teamsheetStatusByWyId.has(wyId)) {
            return null;
        }

        const normalizedLiveName = normalizePlayerName(livePlayer?.name);
        const rosterPlayer = rosterByWyId.get(wyId) || rosterByName.get(normalizedLiveName);
        const teamsheetPlayer = teamsheetPlayerByWyId.get(wyId);
        const hasLiveActiveStatus = livePlayer?.activeStatus !== null && livePlayer?.activeStatus !== undefined;
        const liveActiveStatus = Number(livePlayer?.activeStatus);

        const id = wyId;

        return {
            ...teamsheetPlayer,
            ...rosterPlayer,
            ...livePlayer,
            id,
            wyId,
            name: rosterPlayer?.name || livePlayer?.name || teamsheetPlayer?.name || 'Unknown Player',
            shortName: rosterPlayer?.shortName || rosterPlayer?.name || livePlayer?.name || teamsheetPlayer?.name || 'Unknown',
            position: livePlayer?.position || rosterPlayer?.position || teamsheetPlayer?.position || formationPlaceByWyId.get(wyId),
            formationPlaceId: formationPlaceByWyId.get(wyId) || livePlayer?.position || null,
            activeStatus: hasLiveActiveStatus && Number.isFinite(liveActiveStatus)
                ? liveActiveStatus
                : teamsheetStatusByWyId.get(wyId)
        };
    };

    const hydratedPlayers = (Array.isArray(liveStatePlayers) ? liveStatePlayers : [])
        .map(hydratePlayer)
        .filter(Boolean)
        .filter((player, index, arr) => arr.findIndex((candidate) => String(candidate.id) === String(player.id)) === index);

    return {
        active: hydratedPlayers.filter((player) => player.activeStatus === 1),
        substitutes: hydratedPlayers.filter((player) => player.activeStatus === 2)
    };
};

// Header compaction uses hysteresis so a feed row landing near the threshold
// cannot make the scoreboard flicker between states.
const HEADER_COMPACT_SCROLL_ENTER_PX = 90;
const HEADER_COMPACT_SCROLL_EXIT_PX = 30;
// How long a dropped connection must last before we interrupt with a toast.
const CONNECTION_TOAST_DELAY_MS = 8000;

const formatKickoffText = (rawDate) => {
    if (!rawDate) return 'Date TBD';
    const kickoff = new Date(rawDate);
    if (Number.isNaN(kickoff.getTime())) return 'Date TBD';
    return kickoff.toLocaleString();
};

const MatchStreamTakeoverPrompt = ({ popOpen, onConfirm, onCancel }) => {
    if (!popOpen) return null;

    return (
        <div
            className="popup match-stream-takeover-popup active"
            role="dialog"
            aria-modal="true"
            aria-labelledby="match-stream-takeover-title"
        >
            <div className="pop-content">
                <button
                    type="button"
                    className="pop-close icon-s-cross"
                    aria-label="Close"
                    onClick={onCancel}
                />
                <h3 id="match-stream-takeover-title">Watch match in this tab?</h3>
                <p>Another tab is already watching this match live.</p>
                <div
                    className="form-group submit-block"
                    style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}
                >
                    <button type="button" className="submit-btn" onClick={onConfirm}>
                        Watch here
                    </button>
                    <button type="button" className="submit-btn" onClick={onCancel}>
                        Keep other tab
                    </button>
                </div>
            </div>
        </div>
    );
};

function MatchPage() {
    const {id} = useParams();
    // Get functions from useAuth, including getUser
    const { user: authUser, loading: authLoading } = useAuth();
    // Get user data using getUser and store it in state
    const [currentUser, setCurrentUser] = useState(null);
    const [matchDataState, setMatchDataState] = useState(null);
    const [managedTeamId, setManagedTeamId] = useState(null); // ID of the team managed by the user
    const [userTeamSide, setUserTeamSide] = useState(null); // 'home' or 'away'
    const [pageVisibilityNonce, setPageVisibilityNonce] = useState(0);
    const [matchStreamTakeoverPrompt, setMatchStreamTakeoverPrompt] = useState(null);
    const [matchStreamTakeoverGuardState, setMatchStreamTakeoverGuardState] = useState(EMPTY_MATCH_STREAM_TAKEOVER_GUARD);
    const [quickplayKickoffError, setQuickplayKickoffError] = useState('');
    const quickplayKickoffAttemptedMatchIdRef = useRef(null);
    // -------- Active League & User Teams (league-scoped) --------
    const { activeLeague } = useActiveLeague();
    const leagueId = activeLeague?._id ?? activeLeague?.id ?? null;
    const { data: myTeams, isLoading: isLoadingMyTeams } = useGetTeams(!!authUser && !!leagueId, leagueId);



    // Access socket context (though for this debug button, we can use the imported debugEmitter directly)
    const socketCtx = useContext(SocketContext); 
    const socketStatus = socketCtx?.status;
    const socketId = socketCtx?.socketId || null;
    const sendJoinMatch = socketCtx?.sendJoinMatch;
    const sendLoadGameState = socketCtx?.sendLoadGameState;
    const reconnectSocket = socketCtx?.reconnect;

    // Fetch initial match details
    const { data: initialMatchData, isLoading: isLoadingMatch, error: errorMatch, refetch: refetchMatch } = useGetMatchById(id, {
        staleTime: 0,
        refetchOnMount: 'always'
    });
    const { mutateAsync: kickoffQuickplay } = useQuickplayKickoff();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
            return undefined;
        }

        const handleVisibilityChange = () => {
            if (isMatchPageVisible()) {
                setPageVisibilityNonce((value) => value + 1);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // -------- Determine managedTeamId using league-scoped teams --------
    useEffect(() => {
        if (!initialMatchData || !Array.isArray(myTeams)) return;

        const homeId = initialMatchData.homeTeam?._id;
        const awayId = initialMatchData.awayTeam?._id;

        const matchTeam = myTeams.find((t) => {
            const tid = t._id ?? t.id;
            return tid === homeId || tid === awayId;
        });

        if (matchTeam) {
            const tid = matchTeam._id ?? matchTeam.id;
            setManagedTeamId(tid);
            setUserTeamSide(tid === homeId ? 'home' : 'away');
        } else {
            setManagedTeamId(null);
            setUserTeamSide(null);
        }
    }, [initialMatchData, myTeams]);

    const startQuickplayKickoff = useCallback(async () => {
        if (quickplayKickoffAttemptedMatchIdRef.current === id) return;

        quickplayKickoffAttemptedMatchIdRef.current = id;
        setQuickplayKickoffError('');
        try {
            await kickoffQuickplay();
            if (typeof refetchMatch === 'function') {
                await refetchMatch();
            }
        } catch (error) {
            setQuickplayKickoffError(
                error?.response?.data?.error ||
                error?.message ||
                'Nova could not start this Quick Play match.'
            );
        }
    }, [id, kickoffQuickplay, refetchMatch]);

    const retryQuickplayKickoff = useCallback(() => {
        quickplayKickoffAttemptedMatchIdRef.current = null;
        startQuickplayKickoff();
    }, [startQuickplayKickoff]);

    const quickplayAuthUserId = authUser?._id ?? authUser?.id;
    const quickplayHomeUserId = initialMatchData?.homeUser_id?._id ??
        initialMatchData?.homeUser_id?.id ??
        initialMatchData?.homeUser_id;
    const quickplayHomeTeamId = initialMatchData?.homeTeam?._id ??
        initialMatchData?.homeTeam?.id;
    const quickplayAwayTeamId = initialMatchData?.awayTeam?._id ??
        initialMatchData?.awayTeam?.id;
    const managesQuickplayMatchTeam = Boolean(
        managedTeamId &&
        [quickplayHomeTeamId, quickplayAwayTeamId].some((teamId) => (
            teamId && String(teamId) === String(managedTeamId)
        ))
    );
    // The match API payload omits homeUser_id, so fall back to "the user manages
    // the home team": in quickplay the home side is always the league owner's
    // team, and the kickoff endpoint re-checks real ownership server-side.
    const ownsQuickplayMatch = quickplayHomeUserId
        ? Boolean(
            quickplayAuthUserId &&
            String(quickplayAuthUserId) === String(quickplayHomeUserId)
        )
        : Boolean(
            managedTeamId &&
            quickplayHomeTeamId &&
            String(quickplayHomeTeamId) === String(managedTeamId)
        );
    const shouldStartQuickplayKickoff = Boolean(
        initialMatchData?.gameMode === 'quickplay' &&
        toCanonicalStatus(initialMatchData.status) === 'scheduled' &&
        managesQuickplayMatchTeam &&
        ownsQuickplayMatch
    );

    useEffect(() => {
        if (shouldStartQuickplayKickoff) {
            startQuickplayKickoff();
        }
    }, [shouldStartQuickplayKickoff, startQuickplayKickoff]);

    // When a quickplay match ends, the funnel step bar (fed by the cached
    // quickplay-state query) must unlock its Transfers step right away instead
    // of waiting for the next navigation to refetch.
    const quickplayLiveStatusForFunnel = toCanonicalStatus(
        matchDataState?.status ?? initialMatchData?.status
    );
    const quickplayFunnelInvalidatedRef = useRef(false);
    useEffect(() => {
        if (
            initialMatchData?.gameMode !== 'quickplay' ||
            quickplayLiveStatusForFunnel !== 'completed' ||
            quickplayFunnelInvalidatedRef.current
        ) {
            return;
        }
        quickplayFunnelInvalidatedRef.current = true;
        queryClient.invalidateQueries({ queryKey: ['quickplay-state'] });
    }, [initialMatchData?.gameMode, quickplayLiveStatusForFunnel, queryClient]);

    // Check for active matches (frontend gating for team concurrency)
    const activeStatuses = ['waitingForReady', 'waitingForEngine', 'inProgress'];
    const { data: activeMatchesData } = useGetMatches(
        managedTeamId ? {
            teamId: managedTeamId,
            leagueId: leagueId || undefined,
            status: 'active', // This should be mapped to active statuses by the backend
            limit: 5,
            page: 1
        } : {},
        { enabled: !!managedTeamId }
    );

    // --- Fetch player data for BOTH teams ---
    const opponentTeamId = React.useMemo(() => {
        if (!initialMatchData || !userTeamSide) return null;
        return userTeamSide === 'home' ? initialMatchData.awayTeam?._id : initialMatchData.homeTeam?._id;
    }, [initialMatchData, userTeamSide]);

    const { data: managedTeamPlayers, isLoading: isLoadingManagedPlayers } = useGetTeamPlayers(
        managedTeamId,
        {
            leagueId: activeLeague?._id,
            season: activeLeague?.season,
            enabled: !!managedTeamId,
        }
    );
    const { data: opponentTeamPlayers, isLoading: isLoadingOpponentPlayers } = useGetTeamPlayers(
        opponentTeamId,
        {
            leagueId: activeLeague?._id,
            season: activeLeague?.season,
            enabled: !!opponentTeamId,
        }
    );


    // --- Determine User's Team ---
    useEffect(() => {
        // Set currentUser when authUser is available and not loading
        if (!authLoading && authUser) {
            setCurrentUser(authUser);
        } else if (!authLoading && !authUser) {
            // Handle case where user is not logged in or profile couldn't be fetched
            setCurrentUser(null);
        }
        // Dependency on authUser and authLoading
    }, [authUser, authLoading]);



    // Update local state when initial data loads or changes
    useEffect(() => {
        if (initialMatchData) {
            setMatchDataState((prevState) => {
                if (!prevState) return initialMatchData;

                const prevMatchId = prevState._id || prevState.id;
                const incomingMatchId = initialMatchData._id || initialMatchData.id;
                if (prevMatchId && incomingMatchId && prevMatchId !== incomingMatchId) {
                    return initialMatchData;
                }

                const currentStatus = toCanonicalStatus(prevState.status);
                const incomingStatus = toCanonicalStatus(initialMatchData.status);
                if (shouldIgnoreStaleActiveRefetch(currentStatus, incomingStatus)) {
                    console.log(
                        `[MatchPage] Ignoring stale match refetch that would move ${id} ` +
                        `from ${currentStatus} back to ${incomingStatus}.`
                    );
                    return prevState;
                }

                return initialMatchData;
            });
        }
    }, [initialMatchData, id]);

    // Listen for match starting events via WebSocket
    useEffect(() => {
        let unsubscribeMatchStarting = () => {};

        if (socketCtx && typeof socketCtx.subscribe === 'function') {
            unsubscribeMatchStarting = socketCtx.subscribe('match_starting', (payload) => {
                const payloadMatchId = payload.match_id ?? payload.matchId;
                if (payloadMatchId && payloadMatchId !== id) return;
                matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
                loadGameStateRequestKeyRef.current = null;
                setMatchDataState(prevState => (
                    prevState ? {
                        ...prevState,
                        status: toCanonicalStatus(payload.status) || 'inProgress',
                        ...getLiveCommandAttemptPatch(payload)
                    } : prevState
                ));
                if (typeof toast?.success === 'function') {
                    toast.success('Match is starting!');
                }
            });
        }

        return () => {
            if (typeof unsubscribeMatchStarting === 'function') {
                unsubscribeMatchStarting();
            }
        };
    }, [socketCtx, id]);

    // Listen for match preparing events via WebSocket
    useEffect(() => {
        let unsubscribeMatchPreparing = () => {};
        let unsubscribeMatchAutoStarted;
        if (socketCtx && typeof socketCtx.subscribe === 'function') {
            unsubscribeMatchPreparing = socketCtx.subscribe('match_preparing', (payload) => {
                console.log('Received match_preparing event:', payload);
              const payloadMatchId = payload.match_id ?? payload.matchId;
              if (payloadMatchId && payloadMatchId !== id) {
                  console.log(`[MatchPage match_preparing] Ignoring match_preparing for different match (received: ${payloadMatchId}, current page: ${id})`);
                  return;
              }
              setMatchDataState(prevState => (
                  prevState ? {
                      ...prevState,
                      status: toCanonicalStatus(payload?.status) || 'waitingForEngine',
                      ...getLiveCommandAttemptPatch(payload)
                  } : prevState
              ));
                if (typeof toast?.info === 'function') {
                    toast.info('Engine is booting…');
                }
            });
            // Auto-start (cron-driven). Сервер сам перевёл матч из scheduled в waitingForEngine.
            unsubscribeMatchAutoStarted = socketCtx.subscribe('match_auto_started', (payload) => {
                console.log('Received match_auto_started event:', payload);
                const payloadMatchId = payload.match_id ?? payload.matchId;
                if (payloadMatchId && payloadMatchId !== id) return;
                matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
                loadGameStateRequestKeyRef.current = null;
                setMatchDataState(prevState => (
                    prevState ? {
                        ...prevState,
                        status: toCanonicalStatus(payload?.status) || 'waitingForEngine',
                        ...getLiveCommandAttemptPatch(payload)
                    } : prevState
                ));
                if (typeof toast?.info === 'function') {
                    toast.info('Match has started.');
                }
            });
        }
        return () => {
            if (typeof unsubscribeMatchPreparing === 'function') {
                unsubscribeMatchPreparing();
            }
            if (typeof unsubscribeMatchAutoStarted === 'function') {
                unsubscribeMatchAutoStarted();
            }
        };
    }, [socketCtx, id]);


    // --- KEEP HOOKS AND STATE THAT DO NOT DEPEND ON matchDataState or managedTeamId ---
    const tactics = tacticData; // Keep static data
    const [activeTab, setActiveTab] = React.useState(MATCH_TAB.MATCH);
    // Commentary rows reported by CommentariesBlock. The scoreboard derives the
    // scorer lines and the live minute from this one stream
    // so the feed and the header can never disagree.
    const [feedEvents, setFeedEvents] = React.useState([]);
    const [isHeaderCompact, setIsHeaderCompact] = React.useState(false);
    const [showConnectionToast, setShowConnectionToast] = React.useState(false);
    // Optional engine extras. Absent on older matches, in which case the
    // matching widgets stay hidden.
    const [momentumBuckets, setMomentumBuckets] = React.useState(null);
    const [pressureState, setPressureState] = React.useState(null);
    // Team stats keep their last known value after full time so the stats tab
    // does not empty itself the moment the match completes.
    const [teamStatsSnapshot, setTeamStatsSnapshot] = React.useState(null);
    const [subPopupActive, setSubPopupActive] = React.useState(false);
    const [tacPopupActive, setTacPopupActive] = React.useState(false);
    const [isSubstitutionSubmitting, setIsSubstitutionSubmitting] = React.useState(false);
    const [activeTacticId, setActiveTacticId] = React.useState(null); // Initialize null
    const [substitutionsPlayers, setSubstitutionsPlayers] = React.useState([]); // Initialize empty
    const [activePlayers, setActivePlayers] = React.useState([]); // Initialize empty
    // Track players sent off (store by wyId/id)
    const [sentOffPlayerIds, setSentOffPlayerIds] = React.useState(new Set());
    // GK red card auto-sub management
    const AUTO_GK_SUB_TIMEOUT_MS = 30000;
    const gkAutoSubTimerRef = useRef(null);
    const gkIncidentGuardRef = useRef(false);
    const gkAutoSubContextRef = useRef(null);
    // Injury interruption. The engine pauses the match and waits for this
    // manager; the sheet is opened from the socket handlers, so the ref mirrors
    // the state for them to read.
    const [injuryPause, setInjuryPause] = React.useState(null);
    const injuryPauseRef = useRef(null);
    const injuryActionTakenRef = useRef(false);
    const substitutionSubmittingRef = useRef(false);
    const liveRatingsRef = useRef(new Map()); // wyId/name -> rating
    const latestPlayersRef = useRef({ active: [], subs: [] });
    useEffect(() => {
        latestPlayersRef.current = { active: activePlayers, subs: substitutionsPlayers };
    }, [activePlayers, substitutionsPlayers]);
    
    // Add state for tracking pause status
    const [isPaused, setIsPaused] = useState(false);
    // Tracks if THIS client initiated the current pause so we can hide overlay for them
    const [initiatedPause, setInitiatedPause] = useState(false);
    const [pauseReason, setPauseReason] = useState(null);
    const pauseStateRef = useRef({ isPaused: false, reason: null });

    useEffect(() => {
        pauseStateRef.current = { isPaused, reason: pauseReason };
    }, [isPaused, pauseReason]);

    const markActionPauseState = (reason) => {
        const normalisedReason = normalisePauseReason(reason);
        setInitiatedPause(true);
        setIsPaused(true);
        setPauseReason(normalisedReason);
        pauseStateRef.current = { isPaused: true, reason: normalisedReason };
    };

    const clearActionPauseState = (expectedReason = null) => {
        const normalisedExpectedReason = normalisePauseReason(expectedReason);
        const currentReason = normalisePauseReason(pauseStateRef.current.reason);

        if (
            normalisedExpectedReason &&
            currentReason &&
            currentReason !== normalisedExpectedReason
        ) {
            return;
        }

        setIsPaused(false);
        setPauseReason(null);
        pauseStateRef.current = { isPaused: false, reason: null };
        setInitiatedPause(false);
    };

    /**
     * Opens the injury interruption for this page. The sheet only opens when
     * the injured player belongs to the watching manager AND can be matched to
     * a player on the pitch; otherwise the pause overlay explains the stoppage
     * and the engine resolves it on its own deadline.
     */
    const openInjuryPause = useCallback((injury) => {
        if (!injury) return;

        const playerName = formatPlayerName(injury.player_name);
        const teamContext = String(injury.team_context || '').toLowerCase();
        const isMyTeam = !!userTeamSide && teamContext === userTeamSide;
        const normalisedName = normalizePlayerName(playerName);
        const injuredPlayer = isMyTeam
            ? latestPlayersRef.current.active.find((player) => (
                [player?.name, player?.shortName].some((value) => normalizePlayerName(value) === normalisedName)
            ))
            : null;
        const autoResumeSeconds = Number(injury.auto_resume_seconds);
        const hasBenchOptions = latestPlayersRef.current.subs.length > 0;

        const context = {
            playerName: playerName || 'A player',
            position: String(injury.position || injuredPlayer?.position || ''),
            autoResumeSeconds: Number.isFinite(autoResumeSeconds) && autoResumeSeconds > 0
                ? autoResumeSeconds
                : INJURY_AUTO_RESUME_FALLBACK_SECONDS,
            startedAtClientMs: Date.now(),
            outPlayerId: injuredPlayer?.id ?? null,
            isGoalkeeper: String(injuredPlayer?.position || injury.position || '').toUpperCase() === 'GK',
            showSubstitutionSheet: !!injuredPlayer && hasBenchOptions
        };

        injuryActionTakenRef.current = false;
        injuryPauseRef.current = context;
        setInjuryPause(context);

        if (context.showSubstitutionSheet) {
            // An injury outranks a sheet the manager opened themselves.
            setSubPopupActive(true);
        }
    }, [userTeamSide]);

    /**
     * Ends the injury interruption. Every dismissal path funnels through here:
     * simulation_resumed, the substitution the engine made for the injured
     * player, and match completion.
     *
     * `notifyAutoSubstitution` is set by the two paths that mean the engine hit
     * its deadline; the toast is then only shown when this manager did nothing.
     */
    const resolveInjuryPause = useCallback(({ notifyAutoSubstitution = false } = {}) => {
        const activeInjuryPause = injuryPauseRef.current;
        if (!activeInjuryPause) return;

        injuryPauseRef.current = null;
        setInjuryPause(null);

        if (activeInjuryPause.showSubstitutionSheet) {
            setSubPopupActive(false);
            if (
                notifyAutoSubstitution &&
                !injuryActionTakenRef.current &&
                typeof toast?.info === 'function'
            ) {
                toast.info('Automatic substitution made');
            }
        }

        injuryActionTakenRef.current = false;
    }, []);

    // --- DEBUG TRACING STATE ---
    // Holds latest minute seen to detect out-of-order engine_state_update frames
    const lastMinuteRef = useRef(-1);
    const matchStreamJoinRef = useRef({ key: null, promise: null });
    const joinedMatchStreamRef = useRef(null);
    const leaveMatchStreamKeysRef = useRef(new Set());
    const matchPageMountedRef = useRef(false);
    const currentMatchStreamScopeRef = useRef({ matchId: null, socketId: null, shouldWatch: false });
    const sendLeaveMatchRef = useRef(null);
    const loadGameStateRequestKeyRef = useRef(null);
    const reconnectRequestKeyRef = useRef(null);
    const handledCompletionMatchRef = useRef(null);
    const matchStreamActivityRef = useRef({ matchId: null, socketId: null, at: 0, eventName: null });
    const staleMatchStreamRejoinRef = useRef({ key: null, at: 0 });
    const dismissedMatchStreamTakeoverPromptRef = useRef(null);
    const matchStreamTakeoverGuardRef = useRef(EMPTY_MATCH_STREAM_TAKEOVER_GUARD);
    const showMatchStreamTakeoverPromptFromLiveCommandErrorRef = useRef(() => false);
    const currentMatchStreamStatus = toCanonicalStatus(matchDataState?.status);
    currentMatchStreamScopeRef.current = {
        matchId: id || null,
        socketId,
        shouldWatch: currentMatchStreamStatus === 'scheduled' || ACTIVE_STATUSES_SET.has(currentMatchStreamStatus)
    };
    sendLeaveMatchRef.current = socketCtx?.sendLeaveMatch || null;

    useEffect(() => {
        matchPageMountedRef.current = true;
        return () => {
            matchPageMountedRef.current = false;
        };
    }, []);

    const leaveMatchStreamOnce = useCallback((matchIdToLeave, streamKey) => {
        const sendLeaveMatch = sendLeaveMatchRef.current;
        if (!matchIdToLeave || typeof sendLeaveMatch !== 'function') {
            return;
        }

        const leaveKey = streamKey || `${matchIdToLeave}:unknown`;
        if (leaveMatchStreamKeysRef.current.has(leaveKey)) {
            return;
        }

        leaveMatchStreamKeysRef.current.add(leaveKey);
        Promise.resolve(sendLeaveMatch({ matchId: matchIdToLeave }))
            .catch((error) => {
                console.warn('[MatchPage] Failed to leave match stream:', error?.message || error);
            })
            .finally(() => {
                leaveMatchStreamKeysRef.current.delete(leaveKey);
            });
    }, []);

    const shouldLeaveCancelledMatchStream = useCallback((matchIdToLeave) => {
        if (!matchIdToLeave) {
            return false;
        }

        const latestScope = currentMatchStreamScopeRef.current;
        if (!matchPageMountedRef.current) {
            return true;
        }

        if (latestScope?.matchId !== matchIdToLeave) {
            return true;
        }

        return latestScope?.shouldWatch === false;
    }, []);

    const isCurrentMatchStreamScope = useCallback((matchIdToCheck, socketIdToCheck = null) => {
        if (!matchIdToCheck || !matchPageMountedRef.current) {
            return false;
        }

        const latestScope = currentMatchStreamScopeRef.current;
        if (!latestScope?.matchId || String(latestScope.matchId) !== String(matchIdToCheck)) {
            return false;
        }

        if ((latestScope.socketId || null) !== (socketIdToCheck || null)) {
            return false;
        }

        return latestScope.shouldWatch !== false;
    }, []);

    const markMatchStreamActivity = useCallback((eventName) => {
        if (!id) return;
        matchStreamActivityRef.current = {
            matchId: id,
            socketId,
            at: Date.now(),
            eventName: eventName || 'match_event'
        };
    }, [id, socketId]);

    const setMatchStreamTakeoverGuard = useCallback((guard) => {
        const nextGuard = guard?.matchId
            ? { key: guard.key || null, matchId: guard.matchId, state: guard.state || null }
            : EMPTY_MATCH_STREAM_TAKEOVER_GUARD;
        matchStreamTakeoverGuardRef.current = nextGuard;
        setMatchStreamTakeoverGuardState(nextGuard);
    }, []);

    const getMatchStreamTakeoverGuardForMatch = useCallback((matchIdToCheck = id) => {
        const guard = matchStreamTakeoverGuardState;
        if (!guard?.matchId || !guard?.state) {
            return null;
        }

        if (matchIdToCheck && String(guard.matchId) !== String(matchIdToCheck)) {
            return null;
        }

        return guard;
    }, [id, matchStreamTakeoverGuardState]);

    const clearMatchStreamTakeoverGuard = useCallback((matchIdToClear = null) => {
        const guardedMatchId = matchStreamTakeoverGuardRef.current?.matchId;
        if (!matchIdToClear || String(guardedMatchId) === String(matchIdToClear)) {
            setMatchStreamTakeoverGuard(null);
            dismissedMatchStreamTakeoverPromptRef.current = null;
        }
    }, [setMatchStreamTakeoverGuard]);

    const shouldPauseAutomaticMatchStreamJoin = useCallback((matchIdToJoin) => {
        const guard = getMatchStreamTakeoverGuardForMatch(matchIdToJoin);
        return guard?.state === 'prompt' ||
            guard?.state === 'dismissed' ||
            guard?.state === 'confirming';
    }, [getMatchStreamTakeoverGuardForMatch]);

    const showMatchStreamTakeoverPrompt = useCallback((joinResult, {
        matchId: promptMatchId = id,
        status,
        source = 'join_match'
    } = {}) => {
        if (!isMatchStreamTakeoverBlocked(joinResult) || !isMatchPageVisible()) {
            return false;
        }

        const effectiveMatchId = joinResult?.matchId || promptMatchId;
        if (!effectiveMatchId) {
            return false;
        }

        const promptKey = getMatchStreamTakeoverPromptKey(effectiveMatchId, socketId);
        const takeoverGuard = matchStreamTakeoverGuardRef.current;
        const guardedSameMatch =
            takeoverGuard?.matchId &&
            String(takeoverGuard.matchId) === String(effectiveMatchId);
        const suppressPrompt =
            dismissedMatchStreamTakeoverPromptRef.current === promptKey ||
            (guardedSameMatch && takeoverGuard.state === 'dismissed') ||
            (guardedSameMatch && takeoverGuard.state === 'confirming' && source !== 'confirmed_takeover');

        if (suppressPrompt) {
            return true;
        }

        setMatchStreamTakeoverGuard({
            key: promptKey,
            matchId: effectiveMatchId,
            state: 'prompt'
        });
        setMatchStreamTakeoverPrompt({
            key: promptKey,
            matchId: effectiveMatchId,
            status: toCanonicalStatus(joinResult?.status) || toCanonicalStatus(status),
            source
        });
        return true;
    }, [id, setMatchStreamTakeoverGuard, socketId]);

    const applySuccessfulMatchStreamJoin = useCallback((joinResult, {
        streamKey,
        sourceStatus,
        joinMode,
        eventName = 'join_match'
    } = {}) => {
        const joinedStatus = getMostAdvancedMatchStatus(
            sourceStatus,
            toCanonicalStatus(joinResult?.status) || sourceStatus
        );
        const takeoverGuard = matchStreamTakeoverGuardRef.current;
        const hasBlockingTakeoverGuard =
            String(takeoverGuard?.matchId || '') === String(id || '') &&
            BLOCKING_MATCH_STREAM_TAKEOVER_STATES.has(takeoverGuard?.state);
        const joinCanApply =
            !hasBlockingTakeoverGuard ||
            eventName === 'confirmed_takeover' ||
            eventName === 'stale_stream_rejoin';

        if (!joinCanApply) {
            return;
        }

        const canonicalStreamKey = buildMatchStreamKey(
            id,
            socketId,
            joinedStatus || sourceStatus || 'unknown',
            joinMode || (isMatchPageVisible() ? 'takeover' : 'no-takeover')
        ) || streamKey;

        joinedMatchStreamRef.current = {
            key: canonicalStreamKey,
            matchId: id,
            socketId
        };
        matchStreamJoinRef.current = {
            key: canonicalStreamKey,
            promise: Promise.resolve(joinResult),
            matchId: id,
            socketId
        };
        markMatchStreamActivity(eventName);
        clearMatchStreamTakeoverGuard(id);
        setMatchStreamTakeoverPrompt(null);

        const joinAttemptPatch = getLiveCommandAttemptPatch(joinResult);
        const hasJoinAttemptPatch = Object.prototype.hasOwnProperty.call(joinAttemptPatch, 'liveCommandAttemptId');
        if ((joinedStatus && joinedStatus !== sourceStatus) || hasJoinAttemptPatch) {
            setMatchDataState((prev) => {
                if (!prev) return prev;

                const nextStatus = joinedStatus
                    ? getMostAdvancedMatchStatus(prev.status, joinedStatus)
                    : prev.status;
                const nextAttemptId = hasJoinAttemptPatch
                    ? joinAttemptPatch.liveCommandAttemptId
                    : prev.liveCommandAttemptId;

                if (
                    nextStatus === prev.status &&
                    (!hasJoinAttemptPatch || nextAttemptId === prev.liveCommandAttemptId)
                ) {
                    return prev;
                }

                return {
                    ...prev,
                    status: nextStatus,
                    ...(hasJoinAttemptPatch ? { liveCommandAttemptId: nextAttemptId } : {})
                };
            });
        }

        if (joinedStatus !== 'inProgress' || typeof sendLoadGameState !== 'function') {
            if (joinedStatus && joinedStatus !== sourceStatus && typeof refetchMatch === 'function') {
                refetchMatch();
            }
            return;
        }

        const loadStateKey = `${id}:${socketId || 'connected'}`;
        if (loadGameStateRequestKeyRef.current === loadStateKey) {
            return;
        }
        loadGameStateRequestKeyRef.current = loadStateKey;

        if (typeof refetchMatch === 'function') {
            refetchMatch();
        }
        sendLoadGameState({ matchId: id });
    }, [id, socketId, markMatchStreamActivity, clearMatchStreamTakeoverGuard, refetchMatch, sendLoadGameState]);

    const dismissMatchStreamTakeoverPrompt = useCallback(() => {
        if (matchStreamTakeoverPrompt?.matchId) {
            const promptKey = matchStreamTakeoverPrompt.key ||
                getMatchStreamTakeoverPromptKey(matchStreamTakeoverPrompt.matchId, socketId);
            dismissedMatchStreamTakeoverPromptRef.current = promptKey;
            setMatchStreamTakeoverGuard({
                key: promptKey,
                matchId: matchStreamTakeoverPrompt.matchId,
                state: 'dismissed'
            });
        }
        setMatchStreamTakeoverPrompt(null);
    }, [matchStreamTakeoverPrompt, setMatchStreamTakeoverGuard, socketId]);

    const confirmMatchStreamTakeover = useCallback(() => {
        const prompt = matchStreamTakeoverPrompt;
        if (!prompt?.matchId || typeof sendJoinMatch !== 'function') {
            clearMatchStreamTakeoverGuard(prompt?.matchId);
            setMatchStreamTakeoverPrompt(null);
            return;
        }

        const promptKey = prompt.key || getMatchStreamTakeoverPromptKey(prompt.matchId, socketId);
        setMatchStreamTakeoverGuard({
            key: promptKey,
            matchId: prompt.matchId,
            state: 'confirming'
        });
        dismissedMatchStreamTakeoverPromptRef.current = null;
        setMatchStreamTakeoverPrompt(null);
        const status = toCanonicalStatus(matchDataState?.status);
        const joinMode = 'takeover';
        const streamKey = buildMatchStreamKey(
            prompt.matchId,
            socketId,
            status || prompt.status || 'unknown',
            joinMode
        );
        const confirmedMatchId = prompt.matchId;
        const confirmedSocketId = socketId;
        const clearPendingConfirmedJoin = () => {
            if (matchStreamJoinRef.current?.key === streamKey) {
                matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
            }
        };
        let confirmedJoinPromise;

        try {
            confirmedJoinPromise = Promise.resolve(sendJoinMatch(buildJoinMatchPayload(confirmedMatchId, {
                allowSessionTakeover: true,
                takeoverConfirmed: true
            })));
        } catch (error) {
            confirmedJoinPromise = Promise.reject(error);
        }

        matchStreamJoinRef.current = {
            key: streamKey,
            promise: confirmedJoinPromise,
            matchId: confirmedMatchId,
            socketId: confirmedSocketId
        };

        confirmedJoinPromise
            .then((joinResult) => {
                const joinSucceeded = joinResult === true || !!joinResult?.success;
                if (!isCurrentMatchStreamScope(confirmedMatchId, confirmedSocketId)) {
                    clearPendingConfirmedJoin();
                    if (joinSucceeded && shouldLeaveCancelledMatchStream(confirmedMatchId)) {
                        leaveMatchStreamOnce(confirmedMatchId, streamKey);
                    }
                    return;
                }

                if (!joinSucceeded) {
                    clearPendingConfirmedJoin();
                    showMatchStreamTakeoverPrompt(joinResult, {
                        matchId: confirmedMatchId,
                        status,
                        source: 'confirmed_takeover'
                    });
                    return;
                }

                applySuccessfulMatchStreamJoin(joinResult, {
                    streamKey,
                    sourceStatus: status,
                    joinMode,
                    eventName: 'confirmed_takeover'
                });
            })
            .catch((error) => {
                clearPendingConfirmedJoin();
                if (!isCurrentMatchStreamScope(confirmedMatchId, confirmedSocketId)) {
                    return;
                }

                const response = error?.response;
                if (showMatchStreamTakeoverPrompt(response, {
                    matchId: confirmedMatchId,
                    status,
                    source: 'confirmed_takeover'
                })) {
                    return;
                }
                clearMatchStreamTakeoverGuard(confirmedMatchId);
                console.warn('[MatchPage] Confirmed match stream takeover failed:', error?.message || error);
                if (typeof toast?.error === 'function') {
                    toast.error('Could not watch match in this tab.');
                }
            });
    }, [
        applySuccessfulMatchStreamJoin,
        clearMatchStreamTakeoverGuard,
        isCurrentMatchStreamScope,
        leaveMatchStreamOnce,
        matchDataState?.status,
        matchStreamTakeoverPrompt,
        sendJoinMatch,
        setMatchStreamTakeoverGuard,
        shouldLeaveCancelledMatchStream,
        showMatchStreamTakeoverPrompt,
        socketId
    ]);

    const hasPendingMatchStreamTakeover = useCallback((matchIdToCheck = id) => {
        const promptMatchId = matchStreamTakeoverPrompt?.matchId;
        if (promptMatchId && (!matchIdToCheck || String(promptMatchId) === String(matchIdToCheck))) {
            return true;
        }

        const guard = getMatchStreamTakeoverGuardForMatch(matchIdToCheck);
        return BLOCKING_MATCH_STREAM_TAKEOVER_STATES.has(guard?.state);
    }, [getMatchStreamTakeoverGuardForMatch, id, matchStreamTakeoverPrompt?.matchId]);

    const blockLiveActionForPendingTakeover = useCallback(() => {
        if (!hasPendingMatchStreamTakeover(id)) {
            return false;
        }

        toast.error('Choose where to watch this match before making live changes.');
        return true;
    }, [hasPendingMatchStreamTakeover, id]);

    const showMatchStreamTakeoverPromptFromLiveCommandError = useCallback((error, response, source) => {
        const result = response && typeof response === 'object'
            ? response
            : error?.response || (error && typeof error === 'object' ? error : null);

        if (!isMatchStreamTakeoverBlocked(result)) {
            return false;
        }

        return showMatchStreamTakeoverPrompt({
            success: false,
            matchId: result?.matchId || id,
            status: result?.status || matchDataState?.status,
            ...result
        }, {
            matchId: result?.matchId || id,
            status: result?.status || matchDataState?.status,
            source
        });
    }, [id, matchDataState?.status, showMatchStreamTakeoverPrompt]);
    showMatchStreamTakeoverPromptFromLiveCommandErrorRef.current = showMatchStreamTakeoverPromptFromLiveCommandError;

    useEffect(() => {
        handledCompletionMatchRef.current = null;
        matchStreamActivityRef.current = { matchId: id || null, socketId, at: 0, eventName: null };
        staleMatchStreamRejoinRef.current = { key: null, at: 0 };
    }, [id, socketId]);

    useEffect(() => {
        setMatchStreamTakeoverGuard(null);
        dismissedMatchStreamTakeoverPromptRef.current = null;
        setMatchStreamTakeoverPrompt(null);
    }, [id, setMatchStreamTakeoverGuard, socketId]);
    
    // Subscribe to simulation_paused, simulation_resumed and engine_state_update events
    useEffect(() => {
        let unsubscribePaused = () => {}; // Default to no-op
        let unsubscribeResumed = () => {}; // Default to no-op
        let unsubscribeState = () => {}; // Default to no-op
        let unsubscribeCommentary = () => {}; // Default to no-op
        let unsubscribeSimulationEnded = () => {}; // Default to no-op
        let unsubscribeMatchCompleted = () => {}; // Default to no-op
        let unsubscribeConnectionIssue = () => {}; // Default to no-op

        if (socketCtx && typeof socketCtx.subscribe === 'function') { // Check if subscribe is a function
            console.log('[MatchPage Effect 2] Subscribing to socket events. socketCtx.subscribe is a function.');

            const handleCompletionEvent = (payload, eventName) => {
                const payloadMatchId = getMatchIdFromSocketPayload(payload);
                if (payloadMatchId && payloadMatchId !== id) {
                    console.log(`[MatchPage ${eventName}] Ignoring ${eventName} for different match (received: ${payloadMatchId}, current page: ${id})`);
                    return;
                }
                markMatchStreamActivity(eventName);

                const completionMatchId = payloadMatchId || id;
                const priorCompletion = handledCompletionMatchRef.current;
                const isAuthoritativeCompletion = eventName === 'match_completed';
                const isDuplicateCompletion = priorCompletion?.matchId === completionMatchId;
                if (isDuplicateCompletion && (priorCompletion.authoritative || !isAuthoritativeCompletion)) {
                    console.log(`[MatchPage ${eventName}] Ignoring duplicate completion event for match ${completionMatchId}`);
                    return;
                }
                handledCompletionMatchRef.current = {
                    matchId: completionMatchId,
                    authoritative: Boolean(priorCompletion?.authoritative || isAuthoritativeCompletion)
                };

                const finalScore = getFinalScoreFromCompletionPayload(payload);
                const nextStatus = toCanonicalStatus(getCompletionStatusFromPayload(payload)) || 'completed';
                console.log(`[MatchPage ${eventName}] Match terminal event received.`, payload);

                setMatchDataState(prevState => {
                    if (!prevState) return prevState;

                    return {
                        ...prevState,
                        status: nextStatus,
                        ...(finalScore && {
                            homeScore: finalScore.home,
                            awayScore: finalScore.away,
                            homeTeam: {
                                ...prevState.homeTeam,
                                score: finalScore.home
                            },
                            awayTeam: {
                                ...prevState.awayTeam,
                                score: finalScore.away
                            }
                        })
                    };
                });

                if (refetchMatch && typeof refetchMatch === 'function') {
                    console.log(`[MatchPage ${eventName}] Triggering match data refetch to get final database state`);
                    refetchMatch();
                }

                if (!isDuplicateCompletion && typeof toast?.success === 'function') {
                    const scoreText = `${finalScore?.home ?? 0} - ${finalScore?.away ?? 0}`;
                    const statusText = nextStatus === 'completed' ? 'Match completed!' : `Match ${nextStatus}.`;
                    toast.success(`${statusText} Final Score: ${scoreText}`);
                } else if (!isDuplicateCompletion) {
                    console.error(`[MatchPage ${eventName}] toast.success is NOT a function!`, toast);
                }

                setIsPaused(false);
                setPauseReason(null);
                pauseStateRef.current = { isPaused: false, reason: null };
                setInitiatedPause(false);
                resolveInjuryPause();
                if (gkAutoSubTimerRef.current) {
                    clearTimeout(gkAutoSubTimerRef.current);
                    gkAutoSubTimerRef.current = null;
                }
            };

            unsubscribeConnectionIssue = socketCtx.subscribe('connection_issue', (payload) => {
                const payloadMatchId = payload.match_id ?? payload.matchId;
                if (payloadMatchId && payloadMatchId !== id) {
                    console.log(`[MatchPage connection_issue] Ignoring connection_issue for different match (received: ${payloadMatchId}, current page: ${id})`);
                    return;
                }

                const issueStatus = toCanonicalStatus(payload?.status || payload?.matchStatus);
                if (isNonLiveMatchStatus(issueStatus)) {
                    setMatchDataState((prev) => prev
                        ? { ...prev, status: issueStatus }
                        : prev);
                    setIsPaused(false);
                    setPauseReason(null);
                    pauseStateRef.current = { isPaused: false, reason: null };
                    setInitiatedPause(false);
                    if (typeof refetchMatch === 'function') {
                        refetchMatch();
                    }
                }

                const message = payload?.message || 'The match connection needs attention. Please wait while it recovers.';
                if (payload?.severity === 'error' && typeof toast?.error === 'function') {
                    toast.error(message);
                } else if (typeof toast?.info === 'function') {
                    toast.info(message);
                } else if (typeof toast === 'function') {
                    toast(message);
                } else {
                    console.warn('[MatchPage connection_issue]', message);
                }
            });

            unsubscribePaused = socketCtx.subscribe('simulation_paused', (payload) => {
                // Only process events for the current match page
                const payloadMatchId = payload.match_id ?? payload.matchId;
                if (payloadMatchId && payloadMatchId !== id) {
                    console.log(`[MatchPage simulation_paused] Ignoring simulation_paused for different match (received: ${payloadMatchId}, current page: ${id})`);
                    return;
                }
                markMatchStreamActivity('simulation_paused');
                
                setIsPaused(true);
                setPauseReason(payload.reason);
                pauseStateRef.current = { isPaused: true, reason: payload.reason };
                // If we did NOT initiate this pause, overlay should be visible. Otherwise keep it hidden.
                // (initiatedPause remains as-is)
                console.log(`Game paused. Reason: ${payload.reason}`);

                if (normalisePauseReason(payload.reason) === 'injury') {
                    openInjuryPause(payload.injury);
                }
            });

            unsubscribeResumed = socketCtx.subscribe('simulation_resumed', (payload) => {
                // Only process events for the current match page
                const payloadMatchId = payload.match_id ?? payload.matchId;
                if (payloadMatchId && payloadMatchId !== id) {
                    console.log(`[MatchPage simulation_resumed] Ignoring simulation_resumed for different match (received: ${payloadMatchId}, current page: ${id})`);
                    return;
                }
                markMatchStreamActivity('simulation_resumed');
                
                setIsPaused(false);
                setPauseReason(null);
                pauseStateRef.current = { isPaused: false, reason: null };
                setInitiatedPause(false);
                console.log(`Game resumed`);
                // The server deadline is authoritative: whatever happened to the
                // injured player, the sheet closes here.
                resolveInjuryPause({ notifyAutoSubstitution: true });
                // Cancel any pending GK auto-sub timer on resume
                if (gkAutoSubTimerRef.current) {
                    clearTimeout(gkAutoSubTimerRef.current);
                    gkAutoSubTimerRef.current = null;
                    if (typeof toast?.info === 'function') toast.info('Match resumed. Auto GK replacement cancelled.');
                }
            });

            // Listen for simulation completion
            unsubscribeSimulationEnded = socketCtx.subscribe('simulation_ended', (payload) => {
                handleCompletionEvent(payload, 'simulation_ended');
            });

            unsubscribeMatchCompleted = socketCtx.subscribe('match_completed', (payload) => {
                handleCompletionEvent(payload, 'match_completed');
            });
            
            unsubscribeState = socketCtx.subscribe('engine_state_update', (payload) => {
                // ------------- DEBUG: WS INCOMING FRAME --------------
                try {
                    const seq = payload?.debug_seq ?? performance.now();
                    const homeS = payload?.home_team?.score;
                    const awayS = payload?.away_team?.score;
                    console.log(
                        `%c[WS-IN]  #${seq} min=${payload?.formal_minute ?? '?'} score=${homeS}-${awayS}`,
                        'color:#03a9f4;font-weight:bold'
                    );
                    // Out-of-order detection
                    if (payload?.formal_minute !== undefined && payload.formal_minute < lastMinuteRef.current) {
                        console.warn(
                          `%c[OOO] received minute ${payload.formal_minute} after ${lastMinuteRef.current}`,
                          'background:#ffab00;color:#000'
                        );
                    }
                    if (payload?.formal_minute !== undefined) {
                        lastMinuteRef.current = payload.formal_minute;
                    }
                } catch (e) {
                    console.error('[DEBUG] WS-IN log failed', e);
                }
                // ------------------------------------------------------
                console.log('[MatchPage engine_state_update] Received, payload:', payload);
                
                // 🚨 CRITICAL FIX: Only process events for the current match page
                const esuMatchId = payload.match_id ?? payload.matchId;
                if (esuMatchId && esuMatchId !== id) {
                    console.log(`[MatchPage engine_state_update] ✅ CORRECTLY Ignoring engine_state_update for different match (received: ${esuMatchId}, current page: ${id})`);
                    return;
                }
                markMatchStreamActivity('engine_state_update');
                
                console.log('[MatchPage engine_state_update] Is setIsPaused a function?', typeof setIsPaused === 'function');
                console.log('[MatchPage engine_state_update] Is setPauseReason a function?', typeof setPauseReason === 'function');
                
                // Log the full structure to understand what's available
                console.log('[MatchPage engine_state_update] Full payload structure:', {
                    home_team: payload.home_team,
                    away_team: payload.away_team,
                    home_team_state: payload.home_team_state,
                    away_team_state: payload.away_team_state,
                    home_team_stats: payload.home_team?.stats,
                    away_team_stats: payload.away_team?.stats,
                    substitutions_home: payload.home_team?.stats?.substitutions_made,
                    substitutions_away: payload.away_team?.stats?.substitutions_made
                });
                
                const pauseStatus = String(payload.status || payload.paused_state || '').toLowerCase();
                const gameHalf = String(payload.game_half || payload.gameHalf || '').toLowerCase();
                const derivedIsPaused =
                    payload.isPaused === true ||
                    payload.is_paused_by_user === true ||
                    pauseStatus === 'half_time' ||
                    pauseStatus === 'injury' ||
                    pauseStatus === 'user_pause' ||
                    pauseStatus === 'user_paused';
                const rawDerivedPauseReason =
                    payload.pauseReason ||
                    (pauseStatus === 'half_time' ? 'half_time' : null) ||
                    (pauseStatus === 'user_pause' || pauseStatus === 'user_paused' ? 'user_pause' : null);
                const derivedPauseReason = preserveSpecificPauseReason(
                    rawDerivedPauseReason,
                    pauseStateRef.current.reason
                );
                const isClearlyRunningFrame =
                    payload.is_running === true ||
                    payload.isRunning === true ||
                    pauseStatus === 'running' ||
                    gameHalf === 'second_half';
                const shouldClearHalfTimePause =
                    !derivedIsPaused &&
                    pauseStateRef.current.reason === 'half_time' &&
                    isClearlyRunningFrame;
                const shouldClearInjuryPause =
                    !derivedIsPaused &&
                    normalisePauseReason(pauseStateRef.current.reason) === 'injury' &&
                    isClearlyRunningFrame;

                if (payload.isPaused !== undefined || derivedIsPaused || shouldClearHalfTimePause || shouldClearInjuryPause) {
                    if (typeof setIsPaused === 'function') {
                        const nextIsPaused = shouldClearHalfTimePause || shouldClearInjuryPause ? false : derivedIsPaused;
                        setIsPaused(nextIsPaused);
                        pauseStateRef.current = {
                            ...pauseStateRef.current,
                            isPaused: nextIsPaused
                        };
                    }
                }

                if (derivedPauseReason && typeof setPauseReason === 'function') {
                    setPauseReason(derivedPauseReason);
                    pauseStateRef.current = {
                        ...pauseStateRef.current,
                        reason: derivedPauseReason
                    };
                } else if ((payload.isPaused === false || shouldClearHalfTimePause || shouldClearInjuryPause) && typeof setPauseReason === 'function') {
                    setPauseReason(null);
                    pauseStateRef.current = {
                        ...pauseStateRef.current,
                        reason: null
                    };
                    if (shouldClearHalfTimePause || shouldClearInjuryPause) {
                        setInitiatedPause(false);
                    }
                }

                const homeSubstitutionsFromPayload =
                    payload.home_team?.stats?.substitutions_made ??
                    payload.home_team_state?.substitutions;
                const awaySubstitutionsFromPayload =
                    payload.away_team?.stats?.substitutions_made ??
                    payload.away_team_state?.substitutions;

                const isLoadStateSnapshot = !!(payload.home_team_state || payload.away_team_state);

                // Refresh match data when receiving an explicit save_state snapshot, or when
                // live engine frames do not contain substitution counters.
                if (
                    isLoadStateSnapshot ||
                    homeSubstitutionsFromPayload === undefined ||
                    awaySubstitutionsFromPayload === undefined
                ) {
                    if (refetchMatch && typeof refetchMatch === 'function') {
                        console.log('[MatchPage engine_state_update] Triggering match data refetch for authoritative state');
                        refetchMatch();
                    }
                } else {
                    console.log('[MatchPage engine_state_update] Substitution data available in engine state, skipping refetch');
                }
                
                // Optional momentum series. Keep the longest set we have seen so a
                // partial frame cannot shrink the chart.
                if (Array.isArray(payload.momentum_buckets) && payload.momentum_buckets.length > 0) {
                    setMomentumBuckets((prev) => (
                        Array.isArray(prev) && prev.length > payload.momentum_buckets.length
                            ? prev
                            : payload.momentum_buckets
                    ));
                }

                // Live pressure values feed the header tug-of-war bar.
                const livePressure = payload.pressure_state;
                if (
                    livePressure &&
                    Number.isFinite(Number(livePressure.home)) &&
                    Number.isFinite(Number(livePressure.away))
                ) {
                    setPressureState({
                        home: Number(livePressure.home),
                        away: Number(livePressure.away)
                    });
                }

                // Remember the last team stats we saw so the stats tab survives
                // the completion refetch, which returns the persisted match doc.
                if (payload.home_team?.stats || payload.away_team?.stats) {
                    setTeamStatsSnapshot({
                        home: payload.home_team || null,
                        away: payload.away_team || null
                    });
                }

                // Maintain live ratings map from engine state players if rating present
                try {
                    const ingestFromTeam = (team) => {
                        if (!team || !Array.isArray(team.players)) return;
                        for (const p of team.players) {
                            const key = p.id || p.name;
                            if (key != null && typeof p.rating === 'number') {
                                liveRatingsRef.current.set(String(key), p.rating);
                            }
                        }
                    };
                    ingestFromTeam(payload.home_team);
                    ingestFromTeam(payload.away_team);
                } catch (_) {}

                // Update match data state with engine state for immediate UI updates
                setMatchDataState(prevState => {
                    if (!prevState) return prevState;

                    const previousStateMatchId = prevState._id || prevState.id;
                    const isSameMatchAsPreviousState = !previousStateMatchId || previousStateMatchId === id;

                    const updatedState = {
                        ...prevState,
                         status: (prevState.status === 'inProgress' || isFinalOrEngineErrorMatchStatus(prevState.status)) ? prevState.status : 'inProgress',



                        // Sync substitution counts if provided. Frames can arrive
                        // out of order, so never let one lower a count that this
                        // match has already reported (see
                        // getMostAdvancedSubstitutionCount). State left over from
                        // a different match is not a floor: it is replaced
                        // wholesale by the refetch a moment later.
                        ...(homeSubstitutionsFromPayload !== undefined && {
                            homeUser_subs_made: isSameMatchAsPreviousState
                                ? getMostAdvancedSubstitutionCount(
                                    homeSubstitutionsFromPayload,
                                    prevState.homeUser_subs_made,
                                    prevState.liveState?.home?.substitutionsMade
                                )
                                : getMostAdvancedSubstitutionCount(homeSubstitutionsFromPayload)
                        }),
                        ...(awaySubstitutionsFromPayload !== undefined && {
                            awayUser_subs_made: isSameMatchAsPreviousState
                                ? getMostAdvancedSubstitutionCount(
                                    awaySubstitutionsFromPayload,
                                    prevState.awayUser_subs_made,
                                    prevState.liveState?.away?.substitutionsMade
                                )
                                : getMostAdvancedSubstitutionCount(awaySubstitutionsFromPayload)
                        }),
                        ...(payload.maxSubstitutions !== undefined && {
                            maxSubstitutions: payload.maxSubstitutions
                        }),
                        ...getLiveCommandAttemptPatch(payload),
                        // Merge any relevant engine state data into nested team objects
                        ...(payload.home_team && {
                            homeTeam: {
                                ...prevState.homeTeam,
                                ...payload.home_team
                            }
                        }),
                        ...(payload.away_team && {
                            awayTeam: {
                                ...prevState.awayTeam,
                                ...payload.away_team
                            }
                        })
                    };
                    
                    // ---- DEBUG TRACE FOR STATE WRITE ----
                    try {
                        console.log(
                            `%c[STATE ] wrote home=${updatedState.homeScore} away=${updatedState.awayScore}`,
                            'color:#4caf50;font-weight:bold'
                        );
                    } catch (e) {
                        console.error('[MatchPage DEBUG TRACE] State log failed', e);
                    }
                    // -------------------------------------

                    console.log('[MatchPage engine_state_update] Updated matchDataState with engine data:', {
                        homeSubstitutions: updatedState.homeUser_subs_made,
                        awaySubstitutions: updatedState.awayUser_subs_made,
                        engineHomeSubstitutions: homeSubstitutionsFromPayload,
                        engineAwaySubstitutions: awaySubstitutionsFromPayload
                    });
                    
                    return updatedState;
                });

                // If we detect that our managed team has a GK on the pitch again, cancel any pending auto-sub timer
                try {
                    const teamObj = userTeamSide === 'home' ? payload.home_team : payload.away_team;
                    const hasGK = !!teamObj?.players?.some(pl => pl.position === 'GK');
                    if (hasGK && gkAutoSubTimerRef.current) {
                        clearTimeout(gkAutoSubTimerRef.current);
                        gkAutoSubTimerRef.current = null;
                        gkIncidentGuardRef.current = false;
                        if (typeof toast?.success === 'function') toast.success('GK detected on the pitch. Auto-sub cancelled.');
                    }
                } catch (_) {}
            });

            // Subscribe to commentary updates to catch substitution events
            unsubscribeCommentary = socketCtx.subscribe('commentary_update', (commentaryEvents) => {
                console.log('[MatchPage commentary_update] Received commentary events:', commentaryEvents);
                
                // Only process events for the current match page. Require matchId presence.
                const commentaryMatchId = commentaryEvents.match_id ?? commentaryEvents.matchId;
                if (!commentaryMatchId || commentaryMatchId !== id) {
                    console.log(`[MatchPage commentary_update] Ignoring commentary_update due to missing/mismatched matchId (received: ${commentaryMatchId}, current page: ${id})`);
                    return;
                }
                markMatchStreamActivity('commentary_update');
                
                // Ensure status reflects a live match when commentary arrives
                setMatchDataState(prev => {
                    if (!prev) return prev;
                    if (prev.status === 'inProgress' || isFinalOrEngineErrorMatchStatus(prev.status)) return prev;
                    return { ...prev, status: 'inProgress' };
                });
                
                // Check for substitution events
                const normalisedEvents = Array.isArray(commentaryEvents) ? commentaryEvents : [commentaryEvents];
                const substitutionEvents = normalisedEvents.filter(event => 
                    event.event_type === 'SUBSTITUTION'
                );
                
                substitutionEvents.forEach(subEvent => {
                    console.log('[MatchPage commentary_update] Found substitution event:', subEvent);

                    // Determine which team made the substitution based on team_context
                    if (subEvent.team_context === userTeamSide) {
                        // Do NOT increment substitutionsUsed here: this commentary event is
                        // the narrative echo of a substitution that already incremented the
                        // count in its command success handler (manual and GK auto-sub), so
                        // counting it again showed "1 left" after the first sub. Counts from
                        // other devices reconcile via the authoritative match-data sync.
                        console.log('[MatchPage commentary_update] Substitution was made by user team');
                        // Cancel GK auto timer on any successful user substitution
                        if (gkAutoSubTimerRef.current) {
                            clearTimeout(gkAutoSubTimerRef.current);
                            gkAutoSubTimerRef.current = null;
                            if (typeof toast?.info === 'function') toast.info('Substitution made. Auto GK replacement cancelled.');
                            gkIncidentGuardRef.current = false;
                        }
                    } else {
                        console.log('[MatchPage commentary_update] Substitution was made by opponent team, not updating user count');
                    }
                });

                // An injury pause ends the moment the injured player is replaced,
                // whoever made the change. The engine names the incoming player on
                // a SUBSTITUTION row and carries both names in emphasis, so every
                // name it sends has to be checked.
                const activeInjuryPause = injuryPauseRef.current;
                const injuredPlayerName = normalizePlayerName(activeInjuryPause?.playerName);
                if (activeInjuryPause && injuredPlayerName && substitutionEvents.some((subEvent) => (
                    [
                        subEvent.player_name,
                        subEvent.secondary_player_name,
                        ...(Array.isArray(subEvent.emphasis) ? subEvent.emphasis : [])
                    ].some((candidate) => normalizePlayerName(candidate) === injuredPlayerName)
                ))) {
                    resolveInjuryPause({ notifyAutoSubstitution: true });
                }

                // Check for RED_CARD events and mark player as sent off (disable in dropdown)
                const normalize = (s) => String(s || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
                const findWyIdByName = (name, pool) => {
                    const n = normalize(name);
                    const hit = pool.find(p => [p.name, p.shortName].some(v => normalize(v) === n));
                    return hit?.id ?? null;
                };
                const redCardEvents = normalisedEvents.filter(event => event.event_type === 'RED_CARD');
                redCardEvents.forEach(rcEvent => {
                    const teamCtx = (rcEvent.team_context || '').toLowerCase();
                    if (!userTeamSide || teamCtx !== userTeamSide) return;
                    const pool = [...latestPlayersRef.current.active, ...latestPlayersRef.current.subs];
                    const wyId = findWyIdByName(rcEvent.player_name || rcEvent.playerName || rcEvent.player, pool);
                    if (wyId) {
                        setSentOffPlayerIds(prev => {
                            const next = new Set(prev);
                            next.add(wyId);
                            return next;
                        });
                    }

                    // Detect if the sent-off player was GK; if so, pause first. Only a confirmed pause may open the popup and arm auto-sub.
                    try {
                        const sentOff = pool.find(p => String(p.id) === String(wyId));
                        const wasGK = (sentOff?.position || sentOff?.currentPosition || '').toUpperCase() === 'GK';
                        if (wasGK && !gkIncidentGuardRef.current) {
                            gkIncidentGuardRef.current = true;
                            const latestGkContext = gkAutoSubContextRef.current || {};
                            const latestMatchDataState = latestGkContext.matchDataState;
                            const latestSocketCtx = latestGkContext.socketCtx || socketCtx;
                            const latestMatchId = latestGkContext.matchId || id;
                            pauseGame({
                                matchId: latestMatchId,
                                reason: 'substitution',
                                engineBootAttemptId: getLiveCommandAttemptId(latestMatchDataState),
                                socketCtx: latestSocketCtx,
                                onSuccess: () => {
                                    markActionPauseState('substitution');
                                    setSubPopupActive(true);
                                    if (typeof toast?.custom === 'function' || typeof toast?.info === 'function') {
                                        (toast.custom || toast.info)(
                                            'GK sent off. You have 30 seconds to make a substitution before we auto-select a replacement.'
                                        );
                                    }

                                    if (gkAutoSubTimerRef.current) {
                                        clearTimeout(gkAutoSubTimerRef.current);
                                    }
                                    gkAutoSubTimerRef.current = setTimeout(async () => {
                                        try {
                                            const latestContext = gkAutoSubContextRef.current || {};
                                            const latestMatchDataState = latestContext.matchDataState;
                                            const latestUserTeamSide = latestContext.userTeamSide;
                                            const latestActivePlayers = latestContext.activePlayers || [];
                                            const latestSubstitutionPlayers = latestContext.substitutionsPlayers || [];
                                            const latestMatchId = latestContext.matchId || id;

                                            const teamPlayersOnPitch = latestUserTeamSide === 'home'
                                                ? latestMatchDataState?.homeTeam?.players
                                                : latestMatchDataState?.awayTeam?.players;
                                            const hasGKNow = Array.isArray(teamPlayersOnPitch) && teamPlayersOnPitch.some(pl => pl.position === 'GK');
                                            if (hasGKNow) return; // already resolved

                                            const attackerCodes = new Set(['FW','AM','LW','RW']);
                                            const activeAttackers = latestActivePlayers.filter(p => attackerCodes.has(String(p.position || '').toUpperCase()));
                                            const poolForOut = activeAttackers.length > 0 ? activeAttackers : latestActivePlayers;
                                            const withLive = poolForOut.map(p => {
                                                const key = String(p.id);
                                                const live = liveRatingsRef.current.get(key);
                                                const roster = typeof p.rating === 'number' ? p.rating : 0;
                                                return { p, liveRating: typeof live === 'number' ? live : null, rosterRating: roster };
                                            });
                                            withLive.sort((a,b) => {
                                                const ar = a.liveRating != null ? a.liveRating : a.rosterRating;
                                                const br = b.liveRating != null ? b.liveRating : b.rosterRating;
                                                if (ar !== br) return ar - br; // ascending
                                                return (a.p.formationPlaceId ?? 999) - (b.p.formationPlaceId ?? 999);
                                            });
                                            const outgoing = withLive[0]?.p;

                                            const benchGK = latestSubstitutionPlayers.find(s => String(s.position || '').toUpperCase().includes('GK'));
                                            const benchCB = latestSubstitutionPlayers.find(s => {
                                                const pos = String(s.position || '').toUpperCase();
                                                return pos === 'CB' || pos === 'DC' || pos === 'DF';
                                            });
                                            const incoming = benchGK || benchCB || latestSubstitutionPlayers[0];

                                            if (outgoing && incoming) {
                                                await handleSubstitution({
                                                    activePlayerId: outgoing.id,
                                                    subPlayerId: incoming.id,
                                                    managedTeamId: latestContext.managedTeamId,
                                                    userTeamSide: latestUserTeamSide,
                                                    matchId: latestMatchId,
                                                    engineBootAttemptId: getLiveCommandAttemptId(latestMatchDataState),
                                                    matchStatus: latestMatchDataState?.status,
                                                    currentSubstitutionsUsed: latestContext.substitutionsUsed,
                                                    maxSubstitutions: latestContext.maxSubstitutions,
                                                    socketCtx: latestContext.socketCtx,
                                                    saveTeamsheetAPI: latestContext.saveTeamsheetAPI,
                                                    playerDetails: { activePlayer: outgoing, subPlayer: incoming },
                                                    currentTeamsheetData: latestContext.currentTeamsheetData || null,
                                                    onStateUpdate: ({ incrementSubstitutions, substitutionsUsedTotal, commitState = true, preparedState }) => {
                                                        const updatedActivePlayers = preparedState?.updatedActivePlayers || latestActivePlayers.map(p =>
                                                            p.id === outgoing.id ? { ...incoming, formationPlaceId: outgoing.formationPlaceId, position: 'GK' } : p
                                                        );
                                                        const newSubPlayers = preparedState?.updatedSubPlayers || latestSubstitutionPlayers.filter(p => p.id !== incoming.id && p.id !== outgoing.id);
                                                        if (commitState) {
                                                            setActivePlayers(updatedActivePlayers);
                                                            setSubstitutionsPlayers(newSubPlayers);
                                                            // Absolute total from the engine, for the same
                                                            // reason as the manual path below.
                                                            if (incrementSubstitutions) setSubstitutionsUsed(prev => getMostAdvancedSubstitutionCount(substitutionsUsedTotal, prev));
                                                        }
                                                        return { updatedActivePlayers, updatedSubPlayers: newSubPlayers };
                                                    },
                                                    onSuccess: (msg) => { (toast?.success || console.log)(`Auto-sub: ${msg}`); },
                                                    onError: (err, response) => {
                                                        if (showMatchStreamTakeoverPromptFromLiveCommandErrorRef.current(err, response, 'gk_auto_substitution_command')) {
                                                            return;
                                                        }
                                                        (toast?.error || console.error)(`Auto-sub failed: ${err}`);
                                                    },
                                                    pauseBeforeChange: false,
                                                    resumeAfterChange: true,
                                                    overridePosition: 'GK',
                                                    gkEmergency: true
                                                });
                                            }
                                        } finally {
                                            gkAutoSubTimerRef.current = null;
                                        }
                                    }, AUTO_GK_SUB_TIMEOUT_MS);
                                },
                                onError: (error, response) => {
                                    gkIncidentGuardRef.current = false;
                                    if (showMatchStreamTakeoverPromptFromLiveCommandErrorRef.current(error, response, 'gk_red_card_pause')) {
                                        return;
                                    }
                                    toast.error('Failed to pause the game for goalkeeper replacement. Please try again.');
                                }
                            });
                        }
                    } catch (_) {}
                });

                // --- Live Score Updates: use embedded score data when available ---
                const shouldRevealScore = normalisedEvents.some(isGoalRevealingCommentaryEvent);
                if (shouldRevealScore && normalisedEvents[0]?.score) {
                    const { home, away } = normalisedEvents[0].score;
                    setMatchDataState(prev => ({
                        ...prev,
                        homeScore: home,
                        awayScore: away,
                        homeTeam: { ...prev.homeTeam, score: home },
                        awayTeam: { ...prev.awayTeam, score: away }
                    }));
                } else if (false) { /* legacy score fallback removed */
                    /* Fallback removed logic disabled
                        ['GOAL', 'PENALTY_GOAL', 'OWN_GOAL', 'DIRECT_FREE_KICK_GOAL', 'GOAL_SCORED'].includes(event.event_type)
                    );

                    goalEvents.forEach(goalEvt => {
                        const isHomeGoal = goalEvt.team_context === 'HOME' || goalEvt.team_context === 'home';
                        setMatchDataState(prev => {
                            const beforeHome = (prev?.homeScore ?? prev?.homeTeam?.score ?? 0);
                            if (!prev) return prev;
                            // Determine current scores (fallback order: embedded homeScore/awayScore, team.score, default 0)
                            const currentHome = (prev.homeScore ?? prev.homeTeam?.score ?? 0);
                            const currentAway = (prev.awayScore ?? prev.awayTeam?.score ?? 0);

                            // Avoid double-counting: if the event already matches the current score, skip update
                            // (e.g. when history events replay after reconnect). We assume only +1 per goal.
                            const expectedHome = isHomeGoal ? currentHome + 1 : currentHome;
                            const expectedAway = isHomeGoal ? currentAway : currentAway + 1;

                            // If scores are already ahead of or equal to expected, do not modify – prevents duplicates
                            if ((currentHome >= expectedHome && isHomeGoal) || (currentAway >= expectedAway && !isHomeGoal)) {
                                return prev;
                            }

                            return {
                                ...prev,
                                homeScore: expectedHome,
                                awayScore: expectedAway,
                                homeTeam: { ...prev.homeTeam, score: expectedHome },
                                awayTeam: { ...prev.awayTeam, score: expectedAway }
                            };
                        }); // state setter returns void; removed unnecessary Promise chaining
                        // After state calculated (can't access inside return easily), but use callback trick:
                    });
                    */
                }
            });

            console.log('[MatchPage Effect 2] typeof unsubscribePaused:', typeof unsubscribePaused);
            console.log('[MatchPage Effect 2] typeof unsubscribeResumed:', typeof unsubscribeResumed);
            console.log('[MatchPage Effect 2] typeof unsubscribeState:', typeof unsubscribeState);
            console.log('[MatchPage Effect 2] typeof unsubscribeCommentary:', typeof unsubscribeCommentary);
            console.log('[MatchPage Effect 2] typeof unsubscribeSimulationEnded:', typeof unsubscribeSimulationEnded);
            console.log('[MatchPage Effect 2] typeof unsubscribeMatchCompleted:', typeof unsubscribeMatchCompleted);
            console.log('[MatchPage Effect 2] typeof unsubscribeConnectionIssue:', typeof unsubscribeConnectionIssue);

        } else {
            console.warn('[MatchPage Effect 2] socketCtx or socketCtx.subscribe is not available or not a function.', socketCtx);
        }
            
        return () => {
            console.log('[MatchPage Effect 2 Cleanup] Unsubscribing. typeof unsubscribePaused:', typeof unsubscribePaused, 'typeof unsubscribeResumed:', typeof unsubscribeResumed, 'typeof unsubscribeState:', typeof unsubscribeState, 'typeof unsubscribeCommentary:', typeof unsubscribeCommentary, 'typeof unsubscribeSimulationEnded:', typeof unsubscribeSimulationEnded, 'typeof unsubscribeMatchCompleted:', typeof unsubscribeMatchCompleted, 'typeof unsubscribeConnectionIssue:', typeof unsubscribeConnectionIssue);
            if (typeof unsubscribeConnectionIssue === 'function') {
                unsubscribeConnectionIssue();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeConnectionIssue was not a function during cleanup!');
            }
            if (typeof unsubscribePaused === 'function') {
                unsubscribePaused();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribePaused was not a function during cleanup!');
            }
            if (typeof unsubscribeResumed === 'function') {
                unsubscribeResumed();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeResumed was not a function during cleanup!');
            }
            if (typeof unsubscribeState === 'function') {
                unsubscribeState();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeState was not a function during cleanup!');
            }
            if (typeof unsubscribeCommentary === 'function') {
                unsubscribeCommentary();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeCommentary was not a function during cleanup!');
            }
            if (typeof unsubscribeSimulationEnded === 'function') {
                unsubscribeSimulationEnded();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeSimulationEnded was not a function during cleanup!');
            }
            if (typeof unsubscribeMatchCompleted === 'function') {
                unsubscribeMatchCompleted();
            } else {
                console.error('[MatchPage Effect 2 Cleanup] unsubscribeMatchCompleted was not a function during cleanup!');
            }
        };
    }, [socketCtx, userTeamSide, refetchMatch, id, markMatchStreamActivity, openInjuryPause, resolveInjuryPause]); // Dependency: socketCtx and userTeamSide

    useEffect(() => {
        const cleanupMatchId = id;
        const cleanupSocketId = socketId;

        if (matchStreamJoinRef.current?.matchId !== cleanupMatchId) {
            matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
        }
        if (joinedMatchStreamRef.current?.matchId !== cleanupMatchId) {
            joinedMatchStreamRef.current = null;
        }
        loadGameStateRequestKeyRef.current = null;
        reconnectRequestKeyRef.current = null;

        return () => {
            const latestScope = currentMatchStreamScopeRef.current;
            const isOnlySocketScopeChange =
                latestScope?.matchId === cleanupMatchId &&
                (latestScope?.socketId || null) !== cleanupSocketId;

            if (isOnlySocketScopeChange) {
                return;
            }

            const joinedStream = joinedMatchStreamRef.current;
            const pendingJoinStream = matchStreamJoinRef.current;
            const stillSameJoinedStream =
                joinedStream?.matchId === cleanupMatchId;
            const stillSamePendingJoin =
                pendingJoinStream?.matchId === cleanupMatchId &&
                typeof pendingJoinStream.promise?.then === 'function';

            if (stillSameJoinedStream) {
                leaveMatchStreamOnce(cleanupMatchId, joinedStream.key);
            }

            if (stillSamePendingJoin && pendingJoinStream.key !== joinedStream?.key) {
                const pendingStreamKey = pendingJoinStream.key;
                pendingJoinStream.promise
                    .then((joinResult) => {
                        const joinSucceeded = joinResult === true || !!joinResult?.success;
                        if (joinSucceeded) {
                            leaveMatchStreamOnce(cleanupMatchId, pendingStreamKey);
                        }
                    })
                    .catch((error) => {
                        console.warn('[MatchPage] Pending join_match failed before cleanup leave:', error?.message || error);
                    });
            }

            if (stillSameJoinedStream) {
                joinedMatchStreamRef.current = null;
            }
            if (stillSamePendingJoin && matchStreamJoinRef.current?.key === pendingJoinStream.key) {
                matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
            }
        };
    }, [id, socketId, leaveMatchStreamOnce]);

    useEffect(() => {
        const status = toCanonicalStatus(matchDataState?.status);
        const shouldWatchMatchStream = status === 'scheduled' || ACTIVE_STATUSES_SET.has(status);
        const needsReconnect = socketStatus === 'disconnected' || socketStatus === 'error';
        const reconnectKey = `${id || 'no-match'}:${status || 'unknown'}:${socketStatus || 'unknown'}`;

        if (!shouldWatchMatchStream || !id) {
            reconnectRequestKeyRef.current = null;
            return;
        }

        if (!needsReconnect) {
            reconnectRequestKeyRef.current = null;
            return;
        }

        if (
            reconnectRequestKeyRef.current === reconnectKey ||
            typeof reconnectSocket !== 'function'
        ) {
            return;
        }

        const reconnectStarted = reconnectSocket({ reason: 'match_page_needs_live_socket', matchId: id });
        if (reconnectStarted !== false) {
            reconnectRequestKeyRef.current = reconnectKey;
        }
    }, [id, matchDataState?.status, socketStatus, reconnectSocket]);

    // Attach this browser to the exact match stream, then request a fresh engine snapshot for live matches.
    useEffect(() => {
        const status = toCanonicalStatus(matchDataState?.status);
        const shouldJoinMatchStream = status === 'scheduled' || ACTIVE_STATUSES_SET.has(status);
        if (!shouldJoinMatchStream || !id || socketStatus !== 'connected') {
            return undefined;
        }

        if (shouldPauseAutomaticMatchStreamJoin(id)) {
            return undefined;
        }

        let cancelled = false;
        const allowSessionTakeover = isMatchPageVisible();
        const joinMode = getMatchStreamJoinMode(allowSessionTakeover);
        const streamKey = buildMatchStreamKey(id, socketId, status, joinMode);
        let joinPromise = matchStreamJoinRef.current.key === streamKey
            ? matchStreamJoinRef.current.promise
            : null;

        if (!joinPromise) {
            joinPromise = typeof sendJoinMatch === 'function'
                ? Promise.resolve(sendJoinMatch(buildJoinMatchPayload(id, {
                    allowSessionTakeover
                }))).catch((error) => {
                    console.warn('[MatchPage] Failed to join match stream:', error?.message || error);
                    return error?.response || { success: false, error: error?.message || 'join_match failed' };
                })
                : Promise.resolve({ success: true, status });
            matchStreamJoinRef.current = {
                key: streamKey,
                promise: joinPromise,
                matchId: id,
                socketId
            };
        }

        joinPromise.then((joinResult) => {
            if (cancelled) return;
            const resultStatus = toCanonicalStatus(joinResult?.status);
            const joinFailed = typeof sendJoinMatch === 'function' && joinResult !== true && !joinResult?.success;
            if (joinFailed) {
                showMatchStreamTakeoverPrompt(joinResult, {
                    matchId: id,
                    status: resultStatus || status,
                    source: 'join_match'
                });
                if (matchStreamJoinRef.current.key === streamKey) {
                    matchStreamJoinRef.current = { key: null, promise: null, matchId: null, socketId: null };
                }
                if (joinedMatchStreamRef.current?.key === streamKey) {
                    joinedMatchStreamRef.current = null;
                }
                if (isNonLiveMatchStatus(resultStatus)) {
                    setMatchDataState((prev) => prev
                        ? { ...prev, status: resultStatus }
                        : prev);
                    if (typeof refetchMatch === 'function') {
                        refetchMatch();
                    }
                }
                return;
            }
            applySuccessfulMatchStreamJoin(joinResult, {
                streamKey,
                sourceStatus: status,
                joinMode,
                eventName: 'join_match'
            });
        });

        return () => {
            cancelled = true;
        };
    }, [
        id,
        socketStatus,
        socketId,
        sendJoinMatch,
        matchDataState?.status,
        applySuccessfulMatchStreamJoin,
        showMatchStreamTakeoverPrompt,
        refetchMatch,
        shouldPauseAutomaticMatchStreamJoin,
        pageVisibilityNonce
    ]);

    useEffect(() => {
        const status = toCanonicalStatus(matchDataState?.status);
        if (
            status !== 'inProgress' ||
            !id ||
            socketStatus !== 'connected' ||
            typeof sendJoinMatch !== 'function' ||
            isPaused
        ) {
            return undefined;
        }

        let cancelled = false;

        const checkForStaleMatchStream = () => {
            if (!isMatchPageVisible()) {
                return;
            }

            const takeoverGuard = getMatchStreamTakeoverGuardForMatch(id);
            if (takeoverGuard?.state === 'prompt' || takeoverGuard?.state === 'confirming') {
                return;
            }

            const now = Date.now();
            const activity = matchStreamActivityRef.current;
            const hasCurrentActivity =
                activity?.matchId === id &&
                (activity?.socketId || null) === socketId;
            const lastActivityAt = hasCurrentActivity ? Number(activity.at || 0) : 0;

            if (lastActivityAt > 0 && now - lastActivityAt < LIVE_MATCH_STREAM_STALE_MS) {
                return;
            }

            const rejoinKey = `${id}:${socketId || 'connected'}`;
            const lastRejoin = staleMatchStreamRejoinRef.current;
            if (lastRejoin.key === rejoinKey && now - lastRejoin.at < LIVE_MATCH_STREAM_STALE_MS) {
                return;
            }

            staleMatchStreamRejoinRef.current = { key: rejoinKey, at: now };
            const allowSessionTakeover = takeoverGuard?.state === 'dismissed'
                ? false
                : shouldAllowStaleMatchStreamTakeover();
            Promise.resolve(sendJoinMatch(buildJoinMatchPayload(id, {
                allowSessionTakeover
            })))
                .then((joinResult) => {
                    const joinSucceeded = joinResult === true || !!joinResult?.success;
                    if (!joinSucceeded) {
                        const resultStatus = toCanonicalStatus(joinResult?.status);
                        if (!cancelled && isNonLiveMatchStatus(resultStatus)) {
                            setMatchDataState((prev) => prev
                                ? { ...prev, status: resultStatus }
                                : prev);
                            if (typeof refetchMatch === 'function') {
                                refetchMatch();
                            }
                        }
                        return;
                    }
                    if (cancelled) {
                        if (shouldLeaveCancelledMatchStream(id)) {
                            leaveMatchStreamOnce(id, rejoinKey);
                        }
                        return;
                    }

                    const joinedStatus = getMostAdvancedMatchStatus(status, toCanonicalStatus(joinResult?.status) || status);
                    clearMatchStreamTakeoverGuard(id);
                    const canonicalJoinMode = getMatchStreamJoinMode(isMatchPageVisible());
                    const streamKey = buildMatchStreamKey(id, socketId, joinedStatus || status, canonicalJoinMode);
                    joinedMatchStreamRef.current = {
                        key: streamKey,
                        matchId: id,
                        socketId
                    };
                    matchStreamJoinRef.current = {
                        key: streamKey,
                        promise: Promise.resolve(joinResult),
                        matchId: id,
                        socketId
                    };
                    markMatchStreamActivity('stale_stream_rejoin');

                    const joinAttemptPatch = getLiveCommandAttemptPatch(joinResult);
                    const hasJoinAttemptPatch = Object.prototype.hasOwnProperty.call(joinAttemptPatch, 'liveCommandAttemptId');
                    if ((joinedStatus && joinedStatus !== status) || hasJoinAttemptPatch) {
                        setMatchDataState((prev) => {
                            if (!prev) return prev;
                            const nextStatus = joinedStatus
                                ? getMostAdvancedMatchStatus(prev.status, joinedStatus)
                                : prev.status;
                            return {
                                ...prev,
                                status: nextStatus,
                                ...(hasJoinAttemptPatch ? joinAttemptPatch : {})
                            };
                        });
                    }

                    if (joinedStatus === 'inProgress' && typeof sendLoadGameState === 'function') {
                        if (typeof refetchMatch === 'function') {
                            refetchMatch();
                        }
                        sendLoadGameState({ matchId: id });
                    }
                })
                .catch((error) => {
                    if (showMatchStreamTakeoverPrompt(error?.response, {
                        matchId: id,
                        status,
                        source: 'stale_stream_rejoin'
                    })) {
                        return;
                    }
                    const resultStatus = toCanonicalStatus(error?.response?.status);
                    if (!cancelled && isNonLiveMatchStatus(resultStatus)) {
                        setMatchDataState((prev) => prev
                            ? { ...prev, status: resultStatus }
                            : prev);
                        if (typeof refetchMatch === 'function') {
                            refetchMatch();
                        }
                        return;
                    }
                    console.warn('[MatchPage] Failed to rejoin stale match stream:', error?.message || error);
                });
        };

        const intervalId = setInterval(checkForStaleMatchStream, LIVE_MATCH_STREAM_STALE_CHECK_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [
        id,
        matchDataState?.status,
        socketStatus,
        socketId,
        sendJoinMatch,
        sendLoadGameState,
        refetchMatch,
        isPaused,
        markMatchStreamActivity,
        leaveMatchStreamOnce,
        shouldLeaveCancelledMatchStream,
        clearMatchStreamTakeoverGuard,
        getMatchStreamTakeoverGuardForMatch,
        showMatchStreamTakeoverPrompt
    ]);

    // --- DATA FETCHING BASED ON MANAGED TEAM ---
    // Fetch tactics for the managed team
    const { data: teamTacticsData, isLoading: isLoadingTactics } = useGetTeamTactics(
        managedTeamId, // Fetch for the correct team
        { enabled: !!managedTeamId } // Only run if managedTeamId is set
    );
    const saveTeamTactics = useSaveTeamTactics();

    // Fetch teamsheet/player list for the managed team
    const { data: teamsheetData, isLoading: isLoadingTeamsheet } = useGetTeamsheet(
        managedTeamId, // Fetch for the correct team
        { enabled: !!managedTeamId } // Only run if managedTeamId is set
    );
    const saveTeamsheet = useSaveTeamsheet(); // Assume this mutation exists

    // Fetch the full player roster for the managed team
    const { data: teamPlayersData, isLoading: isLoadingTeamPlayers } = useGetTeamPlayers(
        managedTeamId,
        {
            leagueId: activeLeague?._id,
            season: activeLeague?.season,
            enabled: !!managedTeamId,
        }
    );

    // --- UPDATE TACTIC STATE ---
    useEffect(() => {
        console.log("[Debug Tactic State] Effect triggered.", { teamTacticsData, matchDataState, userTeamSide, managedTeamId });

        // 1) In-progress: prefer live engine single-letter code (e.g., 'A','N','D','C','P','L').
        const liveTacticChar = matchDataState && userTeamSide && matchDataState[userTeamSide + 'Team']?.tactic;
        const liveNumericId = liveTacticChar ? tacticIdReverseMapping[liveTacticChar] : undefined;
        if (toCanonicalStatus(matchDataState?.status) === 'inProgress' && liveNumericId) {
            console.log(`[Debug Tactic State] Using LIVE tactic from engine state update: '${liveTacticChar}', mapped to ID: ${liveNumericId}`);
            setActiveTacticId(liveNumericId);
            return; // nothing else to do
        }

        const matchDocTeam = matchDataState && userTeamSide && matchDataState[userTeamSide + 'Team'];

        // 2) Non-inProgress: prefer the engine-bound teamsheet tactic saved on the match document.
        //    Note: This deliberately precedes the team tactics API to avoid defaulting to 1
        //    when no team tactics record exists.
        const teamsheetTacticId = normaliseTacticIdForUi(matchDocTeam?.teamsheet?.tacticId);
        if (teamsheetTacticId) {
            console.log("[Debug Tactic State] Using match document teamsheet tacticId:", teamsheetTacticId);
            setActiveTacticId(teamsheetTacticId);
            return;
        }

        // 3) Backward-compatible fallback for older match payloads that carried tacticId at team level.
        const matchDocTacticId = normaliseTacticIdForUi(matchDocTeam?.tacticId);
        if (matchDocTacticId) {
            console.log("[Debug Tactic State] Using legacy match document tacticId:", matchDocTacticId);
            setActiveTacticId(matchDocTacticId);
            return;
        }

        // 4) Fallback: use team tactics API value if present.
        if (teamTacticsData?.data?.tacticId) {
            console.log("[Debug Tactic State] Using team tactics API tacticId:", teamTacticsData.data.tacticId);
            setActiveTacticId(teamTacticsData.data.tacticId);
            return;
        }

        // 5) Nothing available – clear state so UI shows "Not Set"
        console.log("[Debug Tactic State] No tactic information available, setting to null.");
        setActiveTacticId(null);
        // Ensure dependency on userTeamSide as well
    }, [teamTacticsData, matchDataState, userTeamSide, managedTeamId]);

    // --- UPDATE PLAYER STATE (prefer match liveState during in-progress matches) ---
    useEffect(() => {
        if (!managedTeamId || isLoadingTeamsheet || isLoadingTeamPlayers) {
            return;
        }

        const fullRoster = teamPlayersData?.data || [];
        const matchStatus = toCanonicalStatus(matchDataState?.status);
        const liveStatePlayers = userTeamSide
            ? matchDataState?.liveState?.[userTeamSide]?.players
            : null;

        if (matchStatus === 'inProgress' && Array.isArray(liveStatePlayers) && liveStatePlayers.length > 0) {
            const hydratedLivePlayers = buildPlayersFromLiveState({
                liveStatePlayers,
                fullRoster,
                fallbackTeamsheet: teamsheetData?.data
            });

            setActivePlayers(hydratedLivePlayers.active);
            setSubstitutionsPlayers(hydratedLivePlayers.substitutes);
            return;
        }

        if (teamsheetData?.data && Array.isArray(fullRoster)) {
            const startersFromSheet = teamsheetData.data.players || [];
            const substitutesFromSheet = teamsheetData.data.subs || [];

            const playerDetailsMap = new Map();
            fullRoster.forEach((player) => {
                if (player.wyId) {
                    playerDetailsMap.set(player.wyId, player);
                }
            });

            const enrichPlayer = (sheetPlayer) => {
                const details = playerDetailsMap.get(sheetPlayer.wyId);
                if (details) {
                    return {
                        ...sheetPlayer,
                        id: sheetPlayer.wyId,
                        name: details.name || details.shortName || 'Unknown',
                        shortName: details.shortName || details.name || 'Unknown',
                        ...details
                    };
                }

                return {
                    ...sheetPlayer,
                    id: sheetPlayer.wyId,
                    name: 'Unknown Player',
                    shortName: 'Unknown'
                };
            };

            setActivePlayers(startersFromSheet.map(enrichPlayer));
            setSubstitutionsPlayers(substitutesFromSheet.map(enrichPlayer));
            return;
        }

        setActivePlayers([]);
        setSubstitutionsPlayers([]);
    }, [
        teamsheetData,
        teamPlayersData,
        managedTeamId,
        isLoadingTeamsheet,
        isLoadingTeamPlayers,
        matchDataState?.status,
        matchDataState?.liveState,
        userTeamSide
    ]);

    function subPopClose(options = {}) {
        const shouldResume = options?.resume !== false;
        const status = toCanonicalStatus(matchDataState?.status);
        const latestPauseState = pauseStateRef.current;
        // Closing the sheet during an injury never resumes the match: a bare
        // RESUME tells the engine to substitute for you, and so does letting its
        // deadline expire. Only injury mode ends here; the stoppage itself stays
        // on the overlay until the engine resumes.
        if (injuryPauseRef.current?.showSubstitutionSheet) {
            const dismissedInjuryPause = { ...injuryPauseRef.current, showSubstitutionSheet: false };
            injuryPauseRef.current = dismissedInjuryPause;
            injuryActionTakenRef.current = false;
            setInjuryPause(dismissedInjuryPause);
        }
        // If the match is in progress and the popup is open and the game is paused
        if (
            shouldResume &&
            status === 'inProgress' &&
            subPopupActive &&
            latestPauseState.isPaused &&
            normalisePauseReason(latestPauseState.reason) === 'substitution'
        ) {
            // Resume the game when canceling
            resumeGame({
                matchId: id,
                engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
                socketCtx,
                onSuccess: (message) => {
                    (toast?.info || toast?.success || console.log)(message);
                    clearActionPauseState('substitution');
                },
                onError: (error, response) => {
                    console.error('Failed to resume after canceling substitution:', error);
                    if (showMatchStreamTakeoverPromptFromLiveCommandError(error, response, 'substitution_cancel_resume')) {
                        return;
                    }
                    toast.error('Failed to resume the game. Please try again.');
                }
            });
        }
        // Cancel any pending GK auto timer when popup is closed manually
        if (gkAutoSubTimerRef.current) {
            clearTimeout(gkAutoSubTimerRef.current);
            gkAutoSubTimerRef.current = null;
        }
        setSubPopupActive(false);
    }

    function tacPopClose(options = {}) {
        const shouldResume = options?.resume !== false;
        const status = toCanonicalStatus(matchDataState?.status);
        const latestPauseState = pauseStateRef.current;
        // If the match is in progress and the popup is open and the game is paused
        if (
            shouldResume &&
            status === 'inProgress' &&
            tacPopupActive &&
            latestPauseState.isPaused &&
            normalisePauseReason(latestPauseState.reason) === 'tactic_change'
        ) {
            // Resume the game when canceling
            resumeGame({
                matchId: id,
                engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
                socketCtx,
                onSuccess: (message) => {
                    (toast?.info || toast?.success || console.log)(message);
                    clearActionPauseState('tactic_change');
                },
                onError: (error, response) => {
                    console.error('Failed to resume after canceling tactic change:', error);
                    if (showMatchStreamTakeoverPromptFromLiveCommandError(error, response, 'tactic_cancel_resume')) {
                        return;
                    }
                    toast.error('Failed to resume the game. Please try again.');
                }
            });
        }
        setTacPopupActive(false);
    }

    // Button click handlers with pause logic
    function handleMakeSubstitutionClick() {
        console.log('[MatchPage] Make Substitution button CLICKED. Setting subPopupActive to true.');
        console.log('[MatchPage] Match status:', matchDataState?.status);
        console.log('[MatchPage] Socket context available:', !!socketCtx);
        console.log('[MatchPage] pauseGame function available:', typeof pauseGame);
        
        // Only pause if the match is in progress
        const status = toCanonicalStatus(matchDataState?.status);
        if (!canSubstituteForStatus(status)) {
            console.warn('[MatchPage] Substitutions are not available for match status:', status);
            return;
        }

        if (status === 'inProgress' && blockLiveActionForPendingTakeover()) {
            return;
        }

        if (status === 'inProgress') {
            console.log('[MatchPage] Match is in progress, attempting to pause...');
            pauseGame({
                matchId: id,
                reason: 'substitution',
                engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
                socketCtx,
                onSuccess: (message) => {
                    console.log('[MatchPage] Game paused for substitution:', message);
                    markActionPauseState('substitution');
                    setSubPopupActive(true);
                },
                onError: (error, response) => {
                    console.error('Failed to pause game for substitution:', error);
                    if (showMatchStreamTakeoverPromptFromLiveCommandError(error, response, 'substitution_pause')) {
                        return;
                    }
                    toast.error('Failed to pause the game. Please try again.');
                }
            });
        } else {
            console.log('[MatchPage] Match not in progress, opening popup without pausing...');
            // For scheduled matches, just open the popup without pausing
            setSubPopupActive(true);
        }
    }

    function handleChangeTacticClick() {
        console.log('[MatchPage] Change Tactic button CLICKED. Setting tacPopupActive to true.');
        
        // Only pause if the match is in progress
        const status = toCanonicalStatus(matchDataState?.status);
        if (!canChangeTacticForStatus(status)) {
            console.warn('[MatchPage] Tactic changes are not available for match status:', status);
            return;
        }

        if (status === 'inProgress' && blockLiveActionForPendingTakeover()) {
            return;
        }

        if (status === 'inProgress') {
            pauseGame({
                matchId: id,
                reason: 'tactic_change',
                engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
                socketCtx,
                onSuccess: (message) => {
                    console.log('[MatchPage] Game paused for tactic change:', message);
                    markActionPauseState('tactic_change');
                    setTacPopupActive(true);
                },
                onError: (error, response) => {
                    console.error('Failed to pause game for tactic change:', error);
                    if (showMatchStreamTakeoverPromptFromLiveCommandError(error, response, 'tactic_pause')) {
                        return;
                    }
                    toast.error('Failed to pause the game. Please try again.');
                }
            });
        } else {
            // For scheduled matches, just open the popup without pausing
            setTacPopupActive(true);
        }
    }

    // Helper function to get actual substitutions used from match data
    const getActualSubstitutionsUsed = () => {
        if (!matchDataState || !userTeamSide) return 0;
        
        const sideLiveStateSubs = Number(matchDataState?.liveState?.[userTeamSide]?.substitutionsMade);
        const homeSubstitutions = Number(matchDataState.homeUser_subs_made) || 0;
        const awaySubstitutions = Number(matchDataState.awayUser_subs_made) || 0;
        const sideDocumentSubs = userTeamSide === 'home' ? homeSubstitutions : awaySubstitutions;
        const result = Number.isFinite(sideLiveStateSubs)
            ? Math.max(sideLiveStateSubs, sideDocumentSubs)
            : sideDocumentSubs;
        
        console.log('[getActualSubstitutionsUsed]', {
            sideLiveStateSubs,
            homeSubstitutions,
            awaySubstitutions,
            userTeamSide,
            result
        });
        
        return result;
    };

    // Add state for tracking substitutions - Fixed initialization from match data
    const [substitutionsUsed, setSubstitutionsUsed] = useState(() => getActualSubstitutionsUsed());
    const MAX_SUBSTITUTIONS = (() => {
        const liveStateValue = Number(matchDataState?.liveState?.maxSubstitutions);
        if (Number.isFinite(liveStateValue) && liveStateValue > 0) return Math.trunc(liveStateValue);

        const matchValue = Number(matchDataState?.maxSubstitutions);
        if (Number.isFinite(matchValue) && matchValue > 0) return Math.trunc(matchValue);

        return 3;
    })();
    gkAutoSubContextRef.current = {
        activePlayers,
        substitutionsPlayers,
        matchDataState,
        substitutionsUsed,
        maxSubstitutions: MAX_SUBSTITUTIONS,
        managedTeamId,
        userTeamSide,
        matchId: id,
        socketCtx,
        saveTeamsheetAPI: saveTeamsheet.mutateAsync,
        currentTeamsheetData: teamsheetData?.data || null
    };

    // Sync substitution count when match data changes
    useEffect(() => {
        const actualCount = getActualSubstitutionsUsed();
        console.log('[MatchPage] Syncing substitution count from match data:', {
            userTeamSide,
            homeSubstitutions: matchDataState?.homeUser_subs_made,
            awaySubstitutions: matchDataState?.awayUser_subs_made,
            actualCount,
            currentSubstitutionsUsed: substitutionsUsed
        });
        setSubstitutionsUsed(actualCount);
    }, [
        matchDataState?.homeUser_subs_made,
        matchDataState?.awayUser_subs_made,
        matchDataState?.liveState?.home?.substitutionsMade,
        matchDataState?.liveState?.away?.substitutionsMade,
        userTeamSide
    ]);

    // --- MAKE SUB FUNCTION (Uses managedTeamId) ---
    async function makeSub(activeId, subId) {
        if (
            toCanonicalStatus(matchDataState?.status) === 'inProgress' &&
            blockLiveActionForPendingTakeover()
        ) {
            return;
        }

        if (substitutionSubmittingRef.current) {
            return;
        }

        // Captured before any await so the resume that follows the command
        // cannot clear it mid-flight.
        const injuryPauseForSub = injuryPauseRef.current;

        substitutionSubmittingRef.current = true;
        setIsSubstitutionSubmitting(true);

        try {
            // Find players within the current state
            const allCurrentPlayers = [...activePlayers, ...substitutionsPlayers];
            const activePlayer = allCurrentPlayers.find(p => p.id === activeId);
            const subPlayer = allCurrentPlayers.find(p => p.id === subId);

            // Get current teamsheet data to pass to the service
            const currentTeamsheetData = teamsheetData?.data || null;

            if (!currentTeamsheetData) {
                toast.error("Unable to access current teamsheet data for substitution");
                return;
            }

            // An injury pause already stopped the engine, so this substitution
            // must not pause it a second time.
            const pausedForSubstitution =
                !!injuryPauseForSub || (
                    toCanonicalStatus(matchDataState?.status) === 'inProgress' &&
                    pauseStateRef.current.isPaused &&
                    normalisePauseReason(pauseStateRef.current.reason) === 'substitution'
                );

            // Marked here, not on entry, so the checks above that bail out early
            // still count as "the manager did nothing about the injury".
            if (injuryPauseForSub) {
                injuryActionTakenRef.current = true;
            }

            // Delegate to the service function with pause/resume flags
            const success = await handleSubstitution({
                activePlayerId: activeId,
                subPlayerId: subId,
                managedTeamId,
                userTeamSide,
                matchId: id,
                engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
                matchStatus: matchDataState.status,
                currentSubstitutionsUsed: substitutionsUsed,
                maxSubstitutions: MAX_SUBSTITUTIONS,
                currentTeamsheetData, // Pass the current teamsheet data
                socketCtx,
                saveTeamsheetAPI: saveTeamsheet.mutateAsync,
                playerDetails: { activePlayer, subPlayer },
                pauseBeforeChange: !pausedForSubstitution,
                resumeAfterChange: true,
                // A keeper going down needs the emergency path so the engine
                // puts the replacement in goal instead of the injured slot.
                overridePosition: injuryPauseForSub?.isGoalkeeper ? 'GK' : null,
                gkEmergency: !!injuryPauseForSub?.isGoalkeeper,
                onStateUpdate: ({ incrementSubstitutions, substitutionsUsedTotal, activePlayer, subPlayer, commitState = true, preparedState }) => {
                    // Build updated active XI: replace outgoing (activePlayer) with incoming (subPlayer)
                    const updatedActivePlayers = preparedState?.updatedActivePlayers || activePlayers.map(p =>
                        p.id === activePlayer.id
                            ? { ...subPlayer, formationPlaceId: p.formationPlaceId }
                            : p
                    );

                    // If for some reason outgoing player wasn't in current activePlayers (edge-cases), ensure we still add incoming
                    if (!updatedActivePlayers.some(p => p.id === subPlayer.id)) {
                        const posId = activePlayer.formationPlaceId;
                        updatedActivePlayers.push({ ...subPlayer, formationPlaceId: posId });
                    }

                    // Build new substitutes list
                    let newSubPlayers = preparedState?.updatedSubPlayers;
                    if (!newSubPlayers && incrementSubstitutions) {
                        // Live match: outgoing cannot return, incoming disappears from bench
                        newSubPlayers = substitutionsPlayers.filter(p => p.id !== subPlayer.id && p.id !== activePlayer.id);
                    } else if (!newSubPlayers) {
                        // Pre-match tweak: swap bench/xi
                        newSubPlayers = substitutionsPlayers
                            .map(p => (p.id === subPlayer.id ? { ...activePlayer, formationPlaceId: null } : p))
                            .filter(p => p.id !== activePlayer.id);
                        if (!newSubPlayers.some(p => p.id === activePlayer.id)) {
                            newSubPlayers.push({ ...activePlayer, formationPlaceId: null });
                        }
                    }

                    // Commit to state
                    if (commitState) {
                        setActivePlayers(updatedActivePlayers);
                        setSubstitutionsPlayers(newSubPlayers);

                        if (incrementSubstitutions) {
                            // The service hands over the engine's absolute total.
                            // Never "prev + 1": a paused engine emits a state
                            // snapshot twice a second, so prev may already count
                            // this substitution, and adding to it showed
                            // "1 of 3 left" after a single change.
                            setSubstitutionsUsed(prev => getMostAdvancedSubstitutionCount(substitutionsUsedTotal, prev));
                        }
                    }

                    return {
                        updatedActivePlayers: updatedActivePlayers,
                        updatedSubPlayers: newSubPlayers
                    };
                },
                onSuccess: (message) => toast.success(message),
                onError: (message, response) => {
                    if (showMatchStreamTakeoverPromptFromLiveCommandError(message, response, 'substitution_command')) {
                        return;
                    }
                    toast.error(message);
                }
            });

            if (success) {
                posthog.capture('match_substitution_made', { match_id: id, active_player_id: activeId, sub_player_id: subId });
                clearActionPauseState(injuryPauseForSub ? 'injury' : 'substitution');
            }

            if (injuryPauseForSub) {
                // A failed injury substitution keeps the sheet up so the manager
                // can retry or play on before the engine deadline expires.
                if (success) {
                    subPopClose({ resume: false });
                } else {
                    injuryActionTakenRef.current = false;
                }
            } else {
                // On success, the service resumes the game. On failure, closing the
                // popup resumes any pause that was created when the popup opened.
                subPopClose({ resume: !success });
            }
        } finally {
            substitutionSubmittingRef.current = false;
            setIsSubstitutionSubmitting(false);
        }
    }

    // --- PLAY ON AFTER AN INJURY (no substitution) ---
    async function playOnWithTenMen() {
        if (!injuryPauseRef.current) {
            return;
        }

        if (
            toCanonicalStatus(matchDataState?.status) === 'inProgress' &&
            blockLiveActionForPendingTakeover()
        ) {
            return;
        }

        injuryActionTakenRef.current = true;

        const success = await resumeGame({
            matchId: id,
            engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
            injuryResolution: 'play_on',
            socketCtx,
            onSuccess: (message) => {
                (toast?.info || toast?.success || console.log)(message);
            },
            onError: (error, response) => {
                console.error('Failed to play on after an injury:', error);
                injuryActionTakenRef.current = false;
                if (showMatchStreamTakeoverPromptFromLiveCommandError(error, response, 'injury_play_on_resume')) {
                    return;
                }
                toast.error('Failed to resume the game. Please try again.');
            }
        });

        if (success) {
            clearActionPauseState('injury');
            subPopClose({ resume: false });
        }
    }

    // --- CHANGE TACTIC FUNCTION (Uses managedTeamId) ---
    async function changeTactic(tacticId) {
        const matchStatus = toCanonicalStatus(matchDataState?.status);
        if (matchStatus === 'inProgress' && blockLiveActionForPendingTakeover()) {
            return;
        }

        const previousTacticId = activeTacticId;
        setActiveTacticId(tacticId); // Optimistic update
        console.log("%%%%%% [MatchPage] changeTactic FUNCTION ENTERED with tacticId:", tacticId, "%%%%%%");

        let currentFormationId = null;
        if (matchDataState && userTeamSide) {
            if (userTeamSide === 'home' && matchDataState.homeTeam?.teamsheet?.formation) {
                currentFormationId = matchDataState.homeTeam.teamsheet.formation;
                console.log("[MatchPage] changeTactic: Found home team formation ID:", currentFormationId);
            } else if (userTeamSide === 'away' && matchDataState.awayTeam?.teamsheet?.formation) {
                currentFormationId = matchDataState.awayTeam.teamsheet.formation;
                console.log("[MatchPage] changeTactic: Found away team formation ID:", currentFormationId);
            } else {
                console.log("[MatchPage] changeTactic: Formation ID not found in homeTeam or awayTeam teamsheet.", { userTeamSide, homeFormation: matchDataState.homeTeam?.teamsheet?.formation, awayFormation: matchDataState.awayTeam?.teamsheet?.formation });
            }
        } else {
            console.log("[MatchPage] changeTactic: matchDataState or userTeamSide is missing for formation ID lookup.", { matchDataStateExists: !!matchDataState, userTeamSide });
        }

        // More explicit check for null or undefined before proceeding
        if (currentFormationId == null) { // Checks for both null and undefined
            toast.error("Could not determine current formation. Tactic not saved.");
            console.error("[MatchPage] changeTactic: currentFormationId is null or undefined BEFORE API call.", { currentFormationId, userTeamSide, matchDataState });
            setActiveTacticId(previousTacticId); // Revert optimistic update
            tacPopClose();
            return; // Exit if formation ID is missing
        }

        console.log("[MatchPage] changeTactic: FINAL CHECK - Attempting to save tactic with ID:", tacticId, "and formationId to be passed:", currentFormationId);

        const pausedForTacticChange =
            matchStatus === 'inProgress' &&
            pauseStateRef.current.isPaused &&
            normalisePauseReason(pauseStateRef.current.reason) === 'tactic_change';

        // Delegate to service with pause flags
        const tacticChangeResult = await handleTacticChange({
            tacticId: tacticId,
            formationId: currentFormationId, // Pass the formation ID
            managedTeamId,
            userTeamSide,
            matchStatus: matchDataState.status,
            matchId: id, // Match ID from route params
            engineBootAttemptId: getLiveCommandAttemptId(matchDataState),
            socketCtx,
            saveTacticAPI: saveTeamTactics.mutateAsync,
            pauseBeforeChange: !pausedForTacticChange,
            resumeAfterChange: true,
            onSuccess: (message) => toast.success(message),
            onError: (message, response) => {
                if (showMatchStreamTakeoverPromptFromLiveCommandError(message, response, 'tactic_command')) {
                    setActiveTacticId(previousTacticId);
                    return;
                }
                toast.error(message);
                setActiveTacticId(previousTacticId); // Revert optimistic update on error
            },
            onResumeError: (message, response) => {
                if (showMatchStreamTakeoverPromptFromLiveCommandError(message, response, 'tactic_resume')) {
                    return;
                }
                toast.error(message || 'Tactic changed, but the game did not resume. Please try again.');
            }
        });

        const tacticChanged = tacticChangeResult?.tacticChanged === true;
        if (tacticChanged) {
            posthog.capture('match_tactic_changed', { match_id: id, tactic_id: tacticId });
        }
        const resumeFailedAfterTacticChange =
            tacticChanged &&
            tacticChangeResult?.resumeAttempted === true &&
            tacticChangeResult?.resumeSucceeded === false;

        if (tacticChanged && matchStatus !== 'inProgress') {
            setMatchDataState((prevMatchData) => patchSavedTacticIntoMatchData({
                matchData: prevMatchData,
                userTeamSide,
                tacticId,
                formation: currentFormationId
            }));
            if (typeof refetchMatch === 'function') {
                refetchMatch();
            }
        }

        if (tacticChanged && !resumeFailedAfterTacticChange) {
            clearActionPauseState('tactic_change');
        }

        tacPopClose({ resume: !tacticChanged || resumeFailedAfterTacticChange });
    }

    // --- RENDER LOGIC ---
    // Ensure all hooks are called unconditionally to preserve hook order across renders.
    // Live score hook: prefer commentary-driven score, fallback to engine after small window
    const isMatchFinished = isTerminalMatchStatus(matchDataState?.status);
    const { liveScore } = useLiveScore(id, { isMatchFinished });
    const navigate = useNavigate();

    const currentStatus = toCanonicalStatus(matchDataState?.status);
    const isLiveStatus = ACTIVE_STATUSES_SET.has(currentStatus);

    // CommentariesBlock owns the de-duplicated, fanned-out commentary stream.
    // The scoreboard reads it from here instead of subscribing a second time.
    const handleFeedEventsChange = useCallback((events) => {
        setFeedEvents(events);
    }, []);

    useEffect(() => {
        setFeedEvents([]);
        setMomentumBuckets(null);
        setTeamStatsSnapshot(null);
        setActiveTab(MATCH_TAB.MATCH);
        setIsHeaderCompact(false);
        setShowConnectionToast(false);
    }, [id]);

    // Compact the sticky scoreboard once the feed is scrolled, with hysteresis
    // so rows crossing the threshold cannot make it flicker.
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            return undefined;
        }

        const handleScroll = () => {
            const offset = window.scrollY || window.pageYOffset || 0;
            setIsHeaderCompact((wasCompact) => {
                if (!wasCompact && offset > HEADER_COMPACT_SCROLL_ENTER_PX) return true;
                if (wasCompact && offset < HEADER_COMPACT_SCROLL_EXIT_PX) return false;
                return wasCompact;
            });
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // A blip is not worth a banner. Only a prolonged loss on a live match earns
    // the toast with the refresh action.
    useEffect(() => {
        if (!isLiveStatus || socketStatus === 'connected') {
            setShowConnectionToast(false);
            return undefined;
        }

        const timerId = setTimeout(() => setShowConnectionToast(true), CONNECTION_TOAST_DELAY_MS);
        return () => clearTimeout(timerId);
    }, [socketStatus, isLiveStatus]);

    const scorers = useMemo(() => deriveScorers(feedEvents), [feedEvents]);
    const liveMinute = useMemo(() => getLatestMinute(feedEvents), [feedEvents]);
    const latestFormalMinute = useMemo(() => getLatestFormalMinute(feedEvents), [feedEvents]);
    const isAtHalfTime = useMemo(() => isHalfTimeBreak(feedEvents), [feedEvents]);
    const finalHomeScore = matchDataState?.homeScore ?? matchDataState?.homeTeam?.score ?? 0;
    const finalAwayScore = matchDataState?.awayScore ?? matchDataState?.awayTeam?.score ?? 0;
    const finalScore = useMemo(() => {
        if (!isMatchFinished) return null;
        return {
            home: finalHomeScore,
            away: finalAwayScore
        };
    }, [isMatchFinished, finalHomeScore, finalAwayScore]);

    // Use loading/error states based on managedTeamId and relevant queries
    if (isLoadingMatch || !matchDataState) {
        return <Spinner />;
    }
    if (errorMatch) {
        return <ErrorMessage message={`Error loading match: ${errorMatch.message}`} />;
    }
    if (quickplayKickoffError) {
        return (
            <ErrorMessage
                message={`Error starting Quick Play: ${quickplayKickoffError}`}
                onRetry={retryQuickplayKickoff}
            />
        );
    }
    // Check if user manages a team AFTER match data has loaded
     if (!managedTeamId && !isLoadingMatch) {
         // Render the page but potentially disable controls or show a message
         // For now, let's assume read-only view if not managing a team
         console.log("Rendering read-only view or limited controls.");
     }

    // --- Dependent Variable Declarations (using matchDataState) ---
    const match = matchDataState;
    const teamOne = match?.homeTeam;
    const teamTwo = match?.awayTeam;
    const isInProgress = currentStatus === 'inProgress';
    const isParticipant = !!managedTeamId;
    const formationOne = findObjectByKeyValue(formationsData, "id", teamOne?.teamsheet?.formation);
    const formationTwo = findObjectByKeyValue(formationsData, "id", teamTwo?.teamsheet?.formation);
    const leagueBase = activeLeague?.slug ? `/${activeLeague.slug}` : '';
    const summaryHref = `${leagueBase}/match/${id}/summary/${teamOne?._id}`;
    const lineupHref = managedTeamId ? `${leagueBase}/team-pitch/${managedTeamId}` : null;
    const showAutoSquadsPostMatchGuidance = (
        match?.status === 'completed' &&
        activeLeague?.gameMode === 'multiplayer' &&
        activeLeague?.autoSquads === true
    );

    // Determine current tactic title based on the correctly managed activeTacticId
    const currentActiveTactic = findObjectByKeyValue(tactics, "id", activeTacticId);
    const liveControlsBlockedByTakeoverPrompt =
        isInProgress && hasPendingMatchStreamTakeover(id);

    // Loading state specific to user's team data
    const isLoadingManagedTeamData = (isLoadingTactics || isLoadingTeamsheet) && !!managedTeamId;

    // --- Scoreboard ---
    const score = (() => {
        if (isTerminalMatchStatus(match?.status)) {
            return {
                home: match.homeScore ?? match.homeTeam?.score ?? 0,
                away: match.awayScore ?? match.awayTeam?.score ?? 0,
                isPlaceholder: false
            };
        }
        if (liveScore) {
            return { home: liveScore.home, away: liveScore.away, isPlaceholder: false };
        }
        if (isInProgress) {
            return {
                home: match.homeScore ?? match.homeTeam?.score ?? 0,
                away: match.awayScore ?? match.awayTeam?.score ?? 0,
                isPlaceholder: false
            };
        }
        return { home: null, away: null, isPlaceholder: true };
    })();

    const kickoffTimeText = (() => {
        if (!match.date) return 'KICK-OFF TBD';
        const kickoff = new Date(match.date);
        if (Number.isNaN(kickoff.getTime())) return 'KICK-OFF TBD';
        return kickoff.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    })();

    const liveMinuteText = latestFormalMinute
        ? (latestFormalMinute.endsWith("'") ? latestFormalMinute : `${latestFormalMinute}'`)
        : `${liveMinute}'`;

    const statusChip = (() => {
        if (isPaused && !isMatchFinished) return { tone: 'paused', text: 'PAUSED' };
        if (isMatchFinished) return { tone: 'ft', text: 'FULL-TIME' };
        if (isAtHalfTime) return { tone: 'ht', text: 'HALF-TIME' };
        if (isInProgress) return { tone: 'live', text: `LIVE ${liveMinuteText}` };
        if (currentStatus === 'waitingForEngine' || currentStatus === 'waitingForReady') {
            return { tone: 'pre', text: 'STARTING' };
        }
        if (currentStatus === 'engineError') return { tone: 'paused', text: 'ERROR' };
        return { tone: 'pre', text: kickoffTimeText };
    })();

    const statusLabel = {
        scheduled: 'Scheduled',
        waitingForReady: 'Waiting for players',
        waitingForEngine: 'Starting up',
        inProgress: 'Live',
        completed: 'Completed',
        forfeited: 'Forfeited',
        cancelled: 'Cancelled',
        engineError: 'Error'
    }[currentStatus];

    const headerMeta = (
        <>
            {formatKickoffText(match.date)}
            <span className="mx-header__status"> ({statusLabel})</span>
        </>
    );

    // --- Stats sources ---
    // Live frames win, the in-memory snapshot covers the completion refetch,
    // and the stats persisted on the match doc cover a full page reload.
    const persistedStatsTeams = currentStatus === 'completed'
        ? buildCompletedMatchStatsTeams(match)
        : null;
    const statsTeamOne = teamOne?.stats
        ? teamOne
        : { ...(teamOne || {}), ...(teamStatsSnapshot?.home || persistedStatsTeams?.home || {}) };
    const statsTeamTwo = teamTwo?.stats
        ? teamTwo
        : { ...(teamTwo || {}), ...(teamStatsSnapshot?.away || persistedStatsTeams?.away || {}) };
    const momentumSeries = momentumBuckets || match?.stats?.momentum_buckets || null;

    // --- Player pools ---
    const homeRoster = (userTeamSide === 'home' ? managedTeamPlayers?.data : opponentTeamPlayers?.data) || [];
    const awayRoster = (userTeamSide === 'away' ? managedTeamPlayers?.data : opponentTeamPlayers?.data) || [];

    const buildLineupPlayers = (team, roster, source) => {
        if (Array.isArray(team?.players) && team.players.length > 0 && source === 'starters') {
            return team.players;
        }
        const sheetPlayers = source === 'starters' ? team?.teamsheet?.players : team?.teamsheet?.subs;
        if (!Array.isArray(sheetPlayers)) return [];
        const rosterByWyId = new Map((roster || []).map((player) => [String(player.wyId), player]));
        return sheetPlayers.map((sheetPlayer) => {
            const rosterPlayer = rosterByWyId.get(String(sheetPlayer.wyId));
            return {
                id: sheetPlayer.wyId,
                name: rosterPlayer?.name || sheetPlayer.name,
                shortName: rosterPlayer?.shortName || rosterPlayer?.name || sheetPlayer.name,
                position: sheetPlayer.position || rosterPlayer?.position
            };
        });
    };

    // The engine streams live_rating and fatigue on its own player objects; the
    // teamsheet-derived pool used by the sheets does not carry them, so merge.
    const engineTeam = userTeamSide ? match?.[`${userTeamSide}Team`] : null;
    const liveMetaByKey = new Map();
    (Array.isArray(engineTeam?.players) ? engineTeam.players : []).forEach((enginePlayer) => {
        if (enginePlayer?.id != null) liveMetaByKey.set(String(enginePlayer.id), enginePlayer);
        const normalisedName = normalizePlayerName(enginePlayer?.name);
        if (normalisedName) liveMetaByKey.set(normalisedName, enginePlayer);
    });

    const withLiveMeta = (player) => {
        const meta =
            liveMetaByKey.get(String(player.id)) ||
            liveMetaByKey.get(normalizePlayerName(player.name)) ||
            liveMetaByKey.get(normalizePlayerName(player.shortName));
        if (!meta) return player;
        return {
            ...player,
            live_rating: meta.live_rating ?? player.live_rating,
            fatigue: meta.fatigue ?? player.fatigue
        };
    };

    const sheetActivePlayers = activePlayers.map((player) => ({
        ...withLiveMeta(player),
        disabled: sentOffPlayerIds.has(player.id)
    }));
    const sheetBenchPlayers = substitutionsPlayers.map(withLiveMeta);

    // Injury mode is only offered to the manager of the injured team; everyone
    // else reads the stoppage on the pause overlay.
    const injurySheetContext = injuryPause?.showSubstitutionSheet
        ? {
            playerName: injuryPause.playerName,
            position: injuryPause.position,
            autoResumeSeconds: injuryPause.autoResumeSeconds,
            startedAtClientMs: injuryPause.startedAtClientMs,
            outPlayerId: injuryPause.outPlayerId
        }
        : null;
    const isInjurySheetOpen = !!injurySheetContext && subPopupActive;

    // --- Action dock ---
    const substitutionsRemaining = Math.max(0, MAX_SUBSTITUTIONS - substitutionsUsed);
    const connectionLost = socketStatus !== 'connected';
    const isSubstitutionDisabled =
        !canSubstituteForStatus(currentStatus) ||
        (isInProgress && connectionLost) ||
        (isInProgress && liveControlsBlockedByTakeoverPrompt) ||
        (isInProgress && substitutionsUsed >= MAX_SUBSTITUTIONS);
    const isTacticDisabled =
        !canChangeTacticForStatus(currentStatus) ||
        (isInProgress && connectionLost) ||
        (isInProgress && liveControlsBlockedByTakeoverPrompt);
    const showActionDock =
        isParticipant &&
        !isLoadingManagedTeamData &&
        !isFinalOrEngineErrorMatchStatus(currentStatus);

    const playerOfTheMatch = isMatchFinished ? derivePlayerOfTheMatch(teamOne, teamTwo) : null;
    const isSummaryUnlocked = currentStatus === 'completed';

    const handleSelectTab = (nextTab) => {
        if (nextTab === MATCH_TAB.SUMMARY) {
            if (!isSummaryUnlocked) {
                toast.error('Match Summary unlocks at full-time.');
                return;
            }
            navigate(summaryHref);
            return;
        }
        setActiveTab(nextTab);
    };

    const resultText = (() => {
        if (currentStatus !== 'completed' || score.isPlaceholder) return null;
        if (score.home === score.away) return 'The points are shared.';
        const winner = score.home > score.away ? teamOne?.name : teamTwo?.name;
        return `${winner || 'The winner'} take all three points.`;
    })();

    // Quick Play: show which side the user manages on the scoreboard.
    const isQuickplayMatch = (match?.gameMode ?? initialMatchData?.gameMode) === 'quickplay';
    const isManagedScoreboardTeam = (team) => {
        if (!isQuickplayMatch || !managedTeamId || !team) return false;
        const teamId = team._id ?? team.id;
        return !!teamId && String(teamId) === String(managedTeamId);
    };
    const teamOneIsManaged = isManagedScoreboardTeam(teamOne);
    const teamTwoIsManaged = isManagedScoreboardTeam(teamTwo);

    return (
        <div className="page-match-x">
            <div className="wrapper">
                <MatchHeader
                    teamOne={teamOne}
                    teamTwo={teamTwo}
                    score={score}
                    statusChip={statusChip}
                    scorers={scorers}
                    pressure={pressureState}
                    isCompact={isHeaderCompact}
                    metaText={headerMeta}
                    connectionStatus={socketStatus}
                    isLiveMatch={isLiveStatus}
                    homeIsManaged={teamOneIsManaged}
                    awayIsManaged={teamTwoIsManaged}
                />

                <div className="mx-body">
                    <aside className="mx-side">
                        {showActionDock && (
                            <>
                                <TutorialTooltip
                                    id="substitutions-match-tooltip"
                                    title="Substitutions and tactical changes"
                                    description="Here you can substitute players or change your tactics. You can make 3 substitutions and 3 tactical changes per game."
                                    className="f-left"
                                />
                                <MatchActionDock
                                    isPreMatch={!isInProgress}
                                    substitutionLabel="Substitution"
                                    substitutionHint={isInProgress
                                        ? `${substitutionsRemaining} of ${MAX_SUBSTITUTIONS} left`
                                        : 'Swap your XI and bench'}
                                    substitutionAriaLabel={isInProgress
                                        ? `Make Substitution (${substitutionsRemaining} left)`
                                        : 'Make Substitution'}
                                    isSubstitutionDisabled={isSubstitutionDisabled}
                                    onSubstitutionClick={handleMakeSubstitutionClick}
                                    tacticHint={`Current: ${currentActiveTactic ? currentActiveTactic.title : 'Not Set'}`}
                                    isTacticDisabled={isTacticDisabled}
                                    onTacticClick={handleChangeTacticClick}
                                />
                            </>
                        )}

                        {!score.isPlaceholder && (
                            <div className="mx-glance">
                                <MatchGlanceCard
                                    title="At a glance"
                                    teamOne={statsTeamOne}
                                    teamTwo={statsTeamTwo}
                                />
                            </div>
                        )}
                    </aside>

                    {/* The tabs live inside the content column so they line up with
                        it on desktop instead of floating across the whole page. */}
                    <main className="mx-main">
                        <MatchTabs
                            activeTab={activeTab}
                            onSelectTab={handleSelectTab}
                            isSummaryUnlocked={isSummaryUnlocked}
                        />

                        {/* The Match pane hides instead of unmounting: CommentariesBlock
                            accumulates the live feed in its own state, and the scoreboard's
                            scorers, pips and clock all derive from it. Unmounting on a tab
                            switch would erase match history mid-game. */}
                        <div style={{ display: activeTab === MATCH_TAB.MATCH ? undefined : 'none' }}>
                            <>
                                {currentStatus === 'scheduled' && (
                                    <MatchPreMatchPanel
                                        kickoffText={kickoffTimeText}
                                        autoStartNote={getScheduledMatchInfoText(match)}
                                        isParticipant={isParticipant}
                                        formationLabel={formationOne && userTeamSide === 'home'
                                            ? formationOne.title
                                            : formationTwo && userTeamSide === 'away'
                                                ? formationTwo.title
                                                : null}
                                        hasSavedLineup={activePlayers.length > 0}
                                        tacticTitle={currentActiveTactic?.title}
                                        lineupHref={lineupHref}
                                        onChangeTactic={handleChangeTacticClick}
                                        isLineupLockEnabled={match?.preMatchLineupLockEnabled === true}
                                    />
                                )}

                                {isMatchFinished && (
                                    <MatchFullTimePanel
                                        label={currentStatus === 'completed'
                                            ? 'FULL-TIME'
                                            : String(statusLabel || currentStatus).toUpperCase()}
                                        homeScore={score.home}
                                        awayScore={score.away}
                                        resultText={resultText}
                                        playerOfTheMatch={playerOfTheMatch}
                                        summaryHref={isSummaryUnlocked ? summaryHref : null}
                                    />
                                )}

                                {isAtHalfTime && !isMatchFinished && (
                                    <MatchGlanceCard
                                        title="First-half snapshot"
                                        teamOne={statsTeamOne}
                                        teamTwo={statsTeamTwo}
                                    />
                                )}

                                <QuickplayPostMatchActions matchStatus={match.status} />
                                {showAutoSquadsPostMatchGuidance && (
                                    <section className="auto-squads-post-match" aria-label="Team management next steps">
                                        <p className="post-match-squad-guidance">
                                            {POST_MATCH_SQUAD_GUIDANCE}
                                        </p>
                                    </section>
                                )}

                                <CommentariesBlock
                                    matchId={id}
                                    matchStatus={matchDataState?.status}
                                    teamOne={teamOne}
                                    teamTwo={teamTwo}
                                    finalScore={finalScore}
                                    onEventsChange={handleFeedEventsChange}
                                />
                            </>
                        </div>

                        {activeTab === MATCH_TAB.STATS && (
                            currentStatus === 'scheduled' ? (
                                <div className="mx-empty">
                                    Match stats appear here from kick-off, and stay after full-time.
                                </div>
                            ) : (
                                <LiveMatchStats
                                    teamOne={statsTeamOne}
                                    teamTwo={statsTeamTwo}
                                    momentumBuckets={momentumSeries}
                                />
                            )
                        )}

                        {activeTab === MATCH_TAB.LINEUPS && (
                            <MatchLineupsPitch
                                homePlayers={buildLineupPlayers(teamOne, homeRoster, 'starters')}
                                awayPlayers={buildLineupPlayers(teamTwo, awayRoster, 'starters')}
                                homeBench={buildLineupPlayers(teamOne, homeRoster, 'subs')}
                                awayBench={buildLineupPlayers(teamTwo, awayRoster, 'subs')}
                                homeTeamName={teamOne?.shortTitle || teamOne?.name || 'Home'}
                                awayTeamName={teamTwo?.shortTitle || teamTwo?.name || 'Away'}
                                homeFormation={formationOne?.title}
                                awayFormation={formationTwo?.title}
                                showRatings={currentStatus !== 'scheduled'}
                            />
                        )}
                    </main>
                </div>
            </div>

            {/* Only the opponent's pause needs an overlay: when a sheet is open for
                your own action the amber chip in the header already says why. The
                injury sheet counts as one of those sheets. */}
            {isPaused && !initiatedPause && !isInjurySheetOpen && (
                <div className="mx-paused" role="status" data-testid="match-paused-overlay">
                    <p className="mx-paused__title">Game Paused</p>
                    <p className="mx-paused__reason">
                        {pauseReason === 'substitution' && 'Making substitution...'}
                        {pauseReason === 'tactic_change' && 'Changing tactics...'}
                        {pauseReason === 'half_time' && 'Half time...'}
                        {pauseReason === 'injury' && `Injury stoppage: ${injuryPause?.playerName || 'A player'} is receiving treatment.`}
                        {!pauseReason && 'Game is paused.'}
                    </p>
                </div>
            )}

            {showConnectionToast && (
                <div className="mx-toast" role="status" data-testid="match-connection-toast">
                    <span>Live connection lost.</span>
                    <button type="button" onClick={() => window.location.reload()}>Refresh</button>
                </div>
            )}

            <MatchStreamTakeoverPrompt
                popOpen={!!matchStreamTakeoverPrompt}
                onConfirm={confirmMatchStreamTakeover}
                onCancel={dismissMatchStreamTakeoverPrompt}
            />

            {isParticipant && (
                <>
                    <SubstitutionSheet
                        isOpen={subPopupActive}
                        onClose={() => subPopClose()}
                        activePlayers={sheetActivePlayers}
                        substitutionsPlayers={sheetBenchPlayers}
                        onConfirm={makeSub}
                        substitutionsUsed={substitutionsUsed}
                        maxSubstitutions={MAX_SUBSTITUTIONS}
                        isLiveMatch={isInProgress}
                        isSubmitting={isSubstitutionSubmitting}
                        injuryContext={injurySheetContext}
                        onPlayOn={playOnWithTenMen}
                    />
                    <TacticSheet
                        isOpen={tacPopupActive}
                        onClose={() => tacPopClose()}
                        tactics={tactics}
                        activeTacticId={activeTacticId}
                        onConfirm={changeTactic}
                    />
                </>
            )}
        </div>
    )
}

export default MatchPage;
